import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import TopUpButton from "../components/TopUpButton";
import {
  Users, Settings, ChevronRight, ChevronLeft, MapPin, Clock,
  Upload, UserPlus, X, ChevronUp, ChevronDown,
  Trash2, Star, Send, AlertTriangle, RotateCcw,
  Sparkles, Plus, Check, FileText, GripVertical, Lightbulb, Save, Briefcase, Pencil, Copy, Link2,
  DollarSign, Linkedin, ShieldCheck, ExternalLink,
  Download, Search, SlidersHorizontal, ShieldAlert, Eye, EyeOff,
  UserCheck, UserX, ArrowRightLeft, Mic,
} from "lucide-react";
import CategoryBadge from "../components/CategoryBadge";

// ── Consent download helper ───────────────────────────────────────────────────

function downloadConsent(candidate, role) {
  const c = candidate.consent;
  if (!c) return;
  const sep = "─".repeat(60);
  const lines = [
    "CANDIDATE CONSENT RECORD",
    "=".repeat(60),
    "",
    `Candidate Name :  ${candidate.name}`,
    `Email          :  ${candidate.email}`,
    `Role           :  ${role.title}`,
    `Company        :  ${role.company_name}`,
    "",
    "CONSENT DETAILS",
    sep,
    `Consented On   :  ${new Date(c.given_at).toLocaleString()}`,
    `Source         :  ${c.source === "recorded_by_company" ? "Recorded by company (offline/email)" : "Apply link (self-submitted)"}`,
    `IP Address     :  ${c.ip_address || "Not captured"}`,
    c.work_authorization ? `Work Auth      :  ${c.work_authorization}` : null,
    c.notice_period_days != null ? `Notice Period  :  ${c.notice_period_days} days` : null,
    c.earliest_start_date ? `Earliest Start :  ${c.earliest_start_date}` : null,
    "",
    "CONSENT AGREEMENT TEXT",
    sep,
    "",
    (c.template_body || "")
      .replace(/\[Full Name\]/g, candidate.name)
      .replace(/\[Company Name\]/g, role.company_name),
    "",
    sep,
    `Record generated : ${new Date().toISOString()}`,
    "This document constitutes a legally binding consent record stored at time of submission.",
  ].filter((l) => l !== null).join("\n");

  const blob = new Blob([lines], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `consent_${candidate.name.replace(/\s+/g, "_").toLowerCase()}_${new Date().toISOString().slice(0, 10)}.txt`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function formatSalary(role) {
  if (!role.salary_min && !role.salary_max) return null;
  const period = role.salary_period === "hourly" ? "/hr" : "/yr";
  const fmt = (n) => n >= 1000 ? `${(n / 1000).toFixed(0)}k` : String(n);
  if (role.salary_min && role.salary_max) return `${fmt(role.salary_min)} – ${fmt(role.salary_max)}${period}`;
  if (role.salary_min) return `from ${fmt(role.salary_min)}${period}`;
  return `up to ${fmt(role.salary_max)}${period}`;
}
import { useProgressSteps } from "../hooks/useProgressSteps";
import { ProgressSteps } from "../components/ui/ProgressSteps";
import useCompanyGated from "../hooks/useCompanyGated";
import ResumePanel from "../components/ResumePanel";

const SEND_INVITES_STEPS = [
  { label: "Preparing invites", duration: 3000 },
  { label: "Generating secure links", duration: 3500 },
  { label: "Sending emails", duration: 0 },
];

// ── helpers ───────────────────────────────────────────────────────────────────

const STATUS_STYLES = {
  active:  "bg-[#10B981]/10 text-[#10B981] border-[#10B981]/30",
  draft:   "bg-secondary text-muted-foreground border-border",
  paused:  "bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/30",
  closed:  "bg-[#E11D48]/10 text-[#E11D48] border-[#E11D48]/30",
};
const ROLE_STATUSES = ["active", "paused", "closed", "draft"];

const CAND_STATUS_STYLES = {
  under_review: "bg-secondary text-muted-foreground border-border",
  shortlisted:  "bg-[#0025D8]/10 text-[#0025D8] border-[#0025D8]/30",
  invited:      "bg-[#10B981]/10 text-[#10B981] border-[#10B981]/30",
  completed:    "bg-[#10B981]/10 text-[#10B981] border-[#10B981]/30",
  hired:        "bg-[#F59E0B]/10 text-[#B45309] border-[#F59E0B]/40",
  rejected:     "bg-[#E11D48]/10 text-[#E11D48] border-[#E11D48]/30",
};
const CAND_STATUS_LABELS = {
  under_review: "Under Review",
  shortlisted:  "Shortlisted",
  invited:      "Invited",
  completed:    "Completed",
  hired:        "Hired",
  rejected:     "Rejected",
};

const STATUS_STEPS = ["under_review", "shortlisted", "invited", "completed", "hired"];
const STATUS_STEP_LABELS = { under_review: "Review", shortlisted: "Shortlisted", invited: "Invited", completed: "Done", hired: "Hired" };

function StatusBar({ status, onSetStatus }) {
  const isRejected = status === "rejected";
  const isHired = status === "hired";
  const currentIdx = STATUS_STEPS.indexOf(status);

  if (isRejected) {
    return (
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-mono text-[9px] px-2 py-0.5 bg-[#E11D48]/10 text-[#E11D48] rounded-lg border border-[#E11D48]/30 tracking-[0.1em] font-bold">
          REJECTED
        </span>
        <button onClick={() => onSetStatus("under_review")}
          className="font-mono text-[9px] text-muted-foreground hover:text-foreground underline transition-colors">
          Undo
        </button>
      </div>
    );
  }

  if (isHired) {
    return (
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-mono text-[9px] px-2 py-0.5 bg-[#F59E0B]/10 text-[#B45309] rounded-lg border border-[#F59E0B]/40 tracking-[0.1em] font-bold flex items-center gap-1">
          <Check className="w-2.5 h-2.5" />HIRED
        </span>
        <button onClick={() => onSetStatus("completed")}
          className="font-mono text-[9px] text-muted-foreground hover:text-foreground underline transition-colors">
          Undo
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <div className="flex items-center">
        {STATUS_STEPS.slice(0, 4).map((step, i) => {
          const isPast = i < currentIdx;
          const isCurrent = i === currentIdx;
          return (
            <React.Fragment key={step}>
              {i > 0 && (
                <div className={`h-px w-5 ${isPast || isCurrent ? "bg-[#0025D8]" : "bg-border"}`} />
              )}
              <button
                onClick={() => onSetStatus(step)}
                title={STATUS_STEP_LABELS[step]}
                className={`w-2.5 h-2.5 rounded-full border-2 transition-all hover:scale-125 ${
                  isCurrent ? "bg-[#0025D8] border-[#0025D8] shadow-sm"
                  : isPast   ? "bg-[#0025D8]/30 border-[#0025D8]/40"
                  :            "bg-white border-border hover:border-[#0025D8]/60"
                }`}
              />
            </React.Fragment>
          );
        })}
      </div>
      <span className="font-mono text-[9px] text-muted-foreground whitespace-nowrap">
        {STATUS_STEP_LABELS[status] || status}
      </span>
      {status === "completed" && (
        <button
          onClick={() => onSetStatus("hired")}
          className="font-mono text-[9px] px-1.5 py-0.5 bg-[#F59E0B]/10 text-[#B45309] rounded-lg border border-[#F59E0B]/40 hover:bg-[#F59E0B]/20 transition-colors whitespace-nowrap">
          Mark Hired →
        </button>
      )}
      <button
        onClick={() => onSetStatus("rejected")}
        title="Reject candidate"
        className="font-mono text-[11px] text-muted-foreground/40 hover:text-[#E11D48] transition-colors leading-none">
        ×
      </button>
    </div>
  );
}

function scoreColor(s) {
  if (s == null) return "text-muted-foreground";
  return s >= 70 ? "text-[#10B981]" : s >= 40 ? "text-[#F59E0B]" : "text-[#E11D48]";
}
function scoreBg(s) {
  if (s == null) return "bg-secondary border-border text-muted-foreground";
  return s >= 70 ? "bg-[#10B981]/10 border-[#10B981]/40 text-[#10B981]"
    : s >= 40 ? "bg-[#F59E0B]/10 border-[#F59E0B]/40 text-[#F59E0B]"
    : "bg-[#E11D48]/10 border-[#E11D48]/40 text-[#E11D48]";
}

// ── Role Header ───────────────────────────────────────────────────────────────

const SENIORITY_LEVELS = ["Junior", "Mid-level", "Senior/Lead"];

function ReassignModal({ role, onClose, onReassigned }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/company/roles/${role.company_role_id}/reassign-options`)
      .then(({ data }) => setOptions(data.options || []))
      .catch(() => toast.error("Could not load employees"))
      .finally(() => setLoading(false));
  }, [role.company_role_id]);

  const handleReassign = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/company/roles/${role.company_role_id}/reassign`, { new_owner_id: selected });
      toast.success(`Role reassigned to ${data.new_owner_name}`);
      onReassigned(data);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reassign role");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl border border-border max-w-sm w-full p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-9 h-9 bg-[#0025D8]/10 flex items-center justify-center flex-shrink-0">
            <ArrowRightLeft className="w-4 h-4 text-[#0025D8]" />
          </div>
          <div>
            <p className="font-semibold text-sm">Reassign Role Ownership</p>
            <p className="text-xs text-muted-foreground mt-0.5">Select the new owner for <strong>{role.title}</strong></p>
          </div>
        </div>
        {loading ? (
          <div className="font-mono text-xs text-muted-foreground py-4 text-center">Loading team members…</div>
        ) : (
          <div className="space-y-1 mb-5 max-h-56 overflow-y-auto">
            {options.map((opt) => (
              <label key={opt.user_id} className="flex items-center gap-3 p-2.5 rounded-xl border border-border cursor-pointer hover:border-[#0025D8]/40 hover:bg-[#0025D8]/3 transition-all">
                <input type="radio" name="reassign_owner" value={opt.user_id} checked={selected === opt.user_id}
                  onChange={() => setSelected(opt.user_id)} className="accent-[#0025D8]" />
                <div>
                  <div className="text-sm font-medium">{opt.name}</div>
                  <div className="font-mono text-[10px] text-muted-foreground">{opt.email}</div>
                </div>
              </label>
            ))}
          </div>
        )}
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} disabled={saving}
            className="px-4 py-2 text-sm rounded-xl border border-border text-muted-foreground hover:text-foreground transition-colors">
            Cancel
          </button>
          <button onClick={handleReassign} disabled={saving || !selected}
            className="px-4 py-2 text-sm font-bold rounded-lg bg-[#0025D8] text-white hover:bg-[#001CB3] transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            {saving ? "Reassigning…" : "Reassign →"}
          </button>
        </div>
      </div>
    </div>
  );
}

function RoleHeader({ role, onStatusChange, onSeniorityChange, onRoleUpdate }) {
  const { user } = useAuth();
  const canEdit = !user || user.role !== "company_employee" || role.company_user_id === user.user_id;
  const isAdmin = user?.role === "company" && user?.sub_role !== "employee";
  const creatorDisabled = isAdmin && role.creator_account_enabled === false;
  const [changing, setChanging] = useState(false);
  const [changingSeniority, setChangingSeniority] = useState(false);
  const [showReassign, setShowReassign] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareText, setShareText] = useState("");
  const [shareFetching, setShareFetching] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);

  const openShare = async () => {
    setShareOpen(true);
    if (shareText) return;
    setShareFetching(true);
    try {
      const applyUrl = `${window.location.origin}/apply/${role.company_role_id}`;
      const res = await api.post(`/company/roles/${role.company_role_id}/linkedin-share-text`, { apply_url: applyUrl });
      setShareText(res.data.post_text);
    } catch {
      toast.error("Could not generate post text");
    } finally {
      setShareFetching(false);
    }
  };

  const copyShareText = () => {
    if (!shareText) return;
    navigator.clipboard.writeText(shareText).then(() => {
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    });
  };

  const changeStatus = async (status) => {
    setChanging(true);
    try { await api.patch(`/company/roles/${role.company_role_id}`, { status }); onStatusChange(status); }
    catch { toast.error("Could not update status"); } finally { setChanging(false); }
  };
  const changeSeniority = async (level) => {
    setChangingSeniority(true);
    try {
      await api.patch(`/company/roles/${role.company_role_id}`, { seniority_override: level });
      onSeniorityChange(level);
      toast.success(`Seniority set to ${level}`);
    } catch { toast.error("Could not update seniority"); } finally { setChangingSeniority(false); }
  };
  const currentSeniority = role.seniority_level || "Mid-level";
  return (
    <div className="border-b border-border bg-white">
      {/* Warning banner — creator disabled/deleted */}
      {creatorDisabled && (
        <div className="flex items-center justify-between gap-3 px-6 py-3 bg-[#F59E0B]/8 border-b border-[#F59E0B]/30">
          <div className="flex items-center gap-2.5">
            <UserX className="w-4 h-4 text-[#B45309] flex-shrink-0" />
            <p className="text-xs text-[#92400E]">
              <span className="font-semibold">{role.created_by_name || "The original creator"}</span> has been disabled.
              Reassign this role to manage it going forward.
            </p>
          </div>
          <button onClick={() => setShowReassign(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold font-mono rounded-lg border border-[#F59E0B]/60 text-[#92400E] hover:bg-[#F59E0B]/20 transition-colors flex-shrink-0">
            <ArrowRightLeft className="w-3 h-3" /> Reassign Now
          </button>
        </div>
      )}
    <div className="p-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <CategoryBadge category={role.category} />
            {role.employment_type && (
              <span className="font-mono text-[10px] px-2 py-0.5 rounded-xl border border-border text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" />{role.employment_type}</span>
            )}
            {role.work_arrangement && (
              <span className="font-mono text-[10px] px-2 py-0.5 rounded-xl border border-border text-muted-foreground flex items-center gap-1"><Briefcase className="w-3 h-3" />{role.work_arrangement}</span>
            )}
            {role.location && (
              <span className="font-mono text-[10px] px-2 py-0.5 rounded-xl border border-border text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3" />{role.location}</span>
            )}
            {formatSalary(role) && (
              <span className="font-mono text-[10px] px-2 py-0.5 rounded-lg border border-[#10B981]/40 text-[#10B981] bg-[#10B981]/5 flex items-center gap-1"><DollarSign className="w-3 h-3" />{formatSalary(role)}</span>
            )}
            {/* Seniority badge — editable dropdown */}
            <div className="relative group">
              <span className="font-mono text-[10px] px-2 py-0.5 border font-semibold tracking-[0.1em] cursor-pointer select-none bg-[#F59E0B]/10 text-[#B45309] border-[#F59E0B]/40 flex items-center gap-1">
                {changingSeniority ? "…" : currentSeniority} ▾
              </span>
              <div className="absolute top-full left-0 mt-1 bg-white rounded-xl border border-border shadow-lg z-20 hidden group-hover:block min-w-[120px]">
                <div className="font-mono text-[9px] text-muted-foreground px-3 py-1.5 tracking-[0.15em] border-b border-border">SENIORITY</div>
                {SENIORITY_LEVELS.filter((s) => s !== currentSeniority).map((s) => (
                  <button key={s} disabled={changingSeniority} onClick={() => changeSeniority(s)}
                    className="w-full text-left font-mono text-[10px] px-3 py-2 hover:bg-[#F59E0B]/10 hover:text-[#B45309] tracking-[0.1em] text-muted-foreground">
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <div className="relative group">
              <span className={`font-mono text-[10px] px-2 py-0.5 border font-semibold uppercase tracking-[0.1em] cursor-pointer select-none ${STATUS_STYLES[role.status] || STATUS_STYLES.active}`}>
                {role.status} ▾
              </span>
              <div className="absolute top-full left-0 mt-1 bg-white rounded-xl border border-border shadow-lg z-20 hidden group-hover:block min-w-[110px]">
                {ROLE_STATUSES.filter((s) => s !== role.status).map((s) => (
                  <button key={s} disabled={changing} onClick={() => changeStatus(s)}
                    className="w-full text-left font-mono text-[10px] px-3 py-2 hover:bg-secondary uppercase tracking-[0.1em] text-muted-foreground hover:text-foreground">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <h1 className="font-display font-black text-3xl tracking-tight text-[#0F172A]">{role.title}</h1>
          {role.end_client && (
            <span className="inline-flex items-center font-mono text-[10px] px-2 py-0.5 rounded-xl border border-border text-muted-foreground mt-1.5">
              End client: {role.end_client}
            </span>
          )}
          <p className="text-sm text-muted-foreground mt-1">
            Posted {new Date(role.created_at).toLocaleDateString()} · {role.candidate_count} candidate{role.candidate_count !== 1 ? "s" : ""}
            {isAdmin && role.created_by_name && (
              <span className="ml-2 font-mono text-[10px]">
                · <UserCheck className="w-3 h-3 inline mb-0.5" /> {role.created_by_name}
                {role.reassigned_from_name && (
                  <span className="text-muted-foreground/60"> (originally {role.reassigned_from_name})</span>
                )}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={openShare}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-mono rounded-lg border border-[#0A66C2]/40 text-[#0A66C2] hover:bg-[#0A66C2] hover:text-white transition-all">
            <Linkedin className="w-3.5 h-3.5" /> Share to LinkedIn
          </button>
          {canEdit && (
            <Link to={`/company/roles/${role.company_role_id}/edit`}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-mono rounded-xl border border-border text-muted-foreground hover:border-[#0025D8] hover:text-[#0025D8] transition-all">
              <Pencil className="w-3.5 h-3.5" /> Edit Role
            </Link>
          )}
          {canEdit && (
            <button onClick={() => role.onDelete && role.onDelete()}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-mono rounded-lg border border-[#E11D48]/30 text-[#E11D48] hover:bg-[#E11D48] hover:text-white transition-all">
              <Trash2 className="w-3.5 h-3.5" /> Delete Role
            </button>
          )}
          {isAdmin && (
            <button onClick={() => setShowReassign(true)}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-mono rounded-xl border border-border text-muted-foreground hover:border-[#0025D8] hover:text-[#0025D8] transition-all">
              <ArrowRightLeft className="w-3.5 h-3.5" /> Reassign
            </button>
          )}
          <Link to="/company/roles"><Button variant="outline" className="rounded-xl text-xs">← All roles</Button></Link>
        </div>
      </div>
      {role.required_skills?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-4 pt-4 border-t border-border">
          {role.required_skills.map((s) => (
            <span key={s} className="font-mono text-[9px] px-1.5 py-0.5 bg-secondary rounded-xl border border-border text-muted-foreground">{s}</span>
          ))}
        </div>
      )}
    </div>
    {showReassign && (
      <ReassignModal
        role={role}
        onClose={() => setShowReassign(false)}
        onReassigned={(data) => onRoleUpdate && onRoleUpdate({ company_user_id: data.new_owner_id, created_by_name: data.new_owner_name, creator_account_enabled: true, reassigned_from_name: data.reassigned_from_name })}
      />
    )}
    {shareOpen && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShareOpen(false)}>
        <div className="bg-white rounded-xl border border-border w-full max-w-lg mx-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <div className="flex items-center gap-2">
              <Linkedin className="w-4 h-4 text-[#0A66C2]" />
              <span className="font-mono text-xs font-semibold tracking-[0.1em] uppercase">Share to LinkedIn</span>
            </div>
            <button onClick={() => setShareOpen(false)} className="text-muted-foreground hover:text-foreground transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
          {/* Body */}
          <div className="px-5 py-4 space-y-4">
            <p className="text-xs text-muted-foreground font-mono">
              Step 1 — copy the post text below, then open LinkedIn and paste it into a new post.
            </p>
            {shareFetching ? (
              <div className="flex items-center gap-2 py-8 justify-center text-muted-foreground text-xs font-mono">
                <div className="w-4 h-4 border-2 border-[#0A66C2] border-t-transparent rounded-full animate-spin" />
                Generating post text…
              </div>
            ) : (
              <textarea
                value={shareText}
                onChange={(e) => setShareText(e.target.value)}
                rows={10}
                className="w-full rounded-xl border border-border text-xs font-mono p-3 resize-none focus:outline-none focus:border-[#0A66C2] transition-colors"
                placeholder="Post text will appear here…"
              />
            )}
            <div className="flex gap-2">
              <button
                onClick={copyShareText}
                disabled={!shareText || shareFetching}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-mono rounded-xl border border-border text-foreground hover:border-[#0A66C2] hover:text-[#0A66C2] transition-all disabled:opacity-40 disabled:cursor-not-allowed">
                {shareCopied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {shareCopied ? "Copied!" : "Copy post text"}
              </button>
              <a
                href="https://www.linkedin.com/feed/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-mono bg-[#0A66C2] text-white hover:bg-[#004182] transition-colors">
                <ExternalLink className="w-3.5 h-3.5" /> Open LinkedIn →
              </a>
            </div>
            {/* Coming soon: LinkedIn Jobs API */}
            <div className="rounded-xl border border-border/50 rounded p-3 bg-secondary/30 flex items-start gap-2.5">
              <Briefcase className="w-3.5 h-3.5 text-muted-foreground/50 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-[10px] font-mono font-semibold text-muted-foreground/60 tracking-[0.1em] uppercase">Post to LinkedIn Jobs — Coming Soon</p>
                <p className="text-[10px] text-muted-foreground/50 mt-0.5">Direct job listing via LinkedIn Jobs API will be available in a future update.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )}
    </div>
  );
}

// ── Tab bar ───────────────────────────────────────────────────────────────────

function TabBar({ active, onChange }) {
  return (
    <div className="flex border-b border-border bg-white">
      {[{ id: "candidates", label: "Candidates", icon: Users }, { id: "setup", label: "Interview Setup", icon: Settings }].map(({ id, label, icon: Icon }) => (
        <button key={id} onClick={() => onChange(id)}
          className={`flex items-center gap-2 px-6 py-4 font-mono text-xs tracking-[0.15em] uppercase border-b-2 transition-colors ${active === id ? "border-[#0025D8] text-[#0025D8]" : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"}`}>
          <Icon className="w-3.5 h-3.5" />{label}
        </button>
      ))}
    </div>
  );
}

// ── Add Candidate Modal ───────────────────────────────────────────────────────

function AddCandidateModal({ role, consentConfig, onClose, onAdded }) {
  const [mode, setMode] = useState(null); // "resume" | "manual"
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzed, setAnalyzed] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", location: "", linkedin_url: "", notes: "" });
  const [consentGiven, setConsentGiven] = useState(false);
  const [consentWorkAuth, setConsentWorkAuth] = useState("");
  const [consentNotice, setConsentNotice] = useState("");
  const [consentStart, setConsentStart] = useState("");
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleFile = async (file) => {
    if (!file) return;
    setAnalyzing(true);
    try {
      const fd = new FormData();
      fd.append("resume_file", file);
      const { data } = await api.post(`/company/roles/${role.company_role_id}/candidates/analyze-resume`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAnalyzed(data);
      setForm({ name: data.name || "", email: data.email || "", phone: data.phone || "", location: data.location || "", linkedin_url: data.linkedin_url || "", notes: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not analyze resume");
    } finally { setAnalyzing(false); }
  };

  const save = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error("Name and email are required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        match_score: analyzed?.match_score ?? null,
        resume_text: analyzed?.resume_text ?? null,
      };
      if (consentConfig?.consent_enabled && consentGiven) {
        payload.consent_given = true;
        if (consentWorkAuth) payload.work_authorization = consentWorkAuth;
        if (consentNotice) payload.notice_period_days = parseInt(consentNotice);
        if (consentStart) payload.earliest_start_date = consentStart;
      }
      const { data } = await api.post(`/company/roles/${role.company_role_id}/candidates`, payload);
      onAdded(data);
      toast.success("Candidate added");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save candidate");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-xl border border-border w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div>
            <div className="font-mono text-[10px] text-muted-foreground tracking-[0.2em]">ADD CANDIDATE</div>
            <div className="font-display font-bold text-lg mt-0.5">{role.title}</div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
        </div>

        {!mode ? (
          <div className="p-6 grid grid-cols-2 gap-4">
            <button onClick={() => setMode("resume")}
              className="rounded-xl border border-border p-6 text-left hover:border-[#0025D8]/40 hover:bg-[#0025D8]/5 transition-all group">
              <Upload className="w-6 h-6 text-muted-foreground group-hover:text-[#0025D8] mb-3 transition-colors" />
              <div className="font-semibold mb-1">Upload Resume</div>
              <p className="text-xs text-muted-foreground">Auto-fill details and get a JD match score from the resume.</p>
            </button>
            <button onClick={() => setMode("manual")}
              className="rounded-xl border border-border p-6 text-left hover:border-[#0025D8]/40 hover:bg-[#0025D8]/5 transition-all group">
              <UserPlus className="w-6 h-6 text-muted-foreground group-hover:text-[#0025D8] mb-3 transition-colors" />
              <div className="font-semibold mb-1">Manual Entry</div>
              <p className="text-xs text-muted-foreground">Enter candidate details directly — no resume required.</p>
            </button>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            {mode === "resume" && !analyzed && (
              <div>
                <input ref={fileRef} type="file" accept=".pdf,.docx,.doc,.txt" className="hidden"
                  onChange={(e) => handleFile(e.target.files[0])} />
                {analyzing ? (
                  <div className="border border-dashed border-border p-10 text-center">
                    <RotateCcw className="w-6 h-6 text-[#0025D8] animate-spin mx-auto mb-3" />
                    <p className="font-mono text-xs text-muted-foreground">Analyzing resume…</p>
                  </div>
                ) : (
                  <div
                    className="rounded-xl border-2 border-dashed border-border p-10 text-center cursor-pointer hover:border-[#0025D8]/40 hover:bg-[#0025D8]/5 transition-all"
                    onClick={() => fileRef.current?.click()}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}>
                    <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
                    <p className="font-semibold mb-1">Drop resume here or click to browse</p>
                    <p className="text-xs text-muted-foreground">PDF, DOCX, DOC, TXT</p>
                  </div>
                )}
              </div>
            )}

            {(mode === "manual" || analyzed) && (
              <>
                {analyzed && (
                  <div className={`border p-3 flex items-center gap-3 ${scoreBg(analyzed.match_score)}`}>
                    <div className="flex-1">
                      <div className="font-mono text-[10px] tracking-[0.2em] mb-0.5">JD MATCH SCORE</div>
                      <div className="font-display font-bold text-2xl">
                        {analyzed.match_score != null ? `${Math.round(analyzed.match_score)}%` : "—"}
                      </div>
                    </div>
                    {analyzed.summary && <p className="text-xs flex-1">{analyzed.summary}</p>}
                  </div>
                )}
                {[
                  { k: "name", label: "Full Name *", type: "text" },
                  { k: "email", label: "Email *", type: "email" },
                  { k: "phone", label: "Phone", type: "text" },
                  { k: "location", label: "Location", type: "text" },
                  { k: "linkedin_url", label: "LinkedIn URL", type: "url" },
                ].map(({ k, label, type }) => (
                  <div key={k}>
                    <label className="font-mono text-[10px] tracking-[0.2em] text-muted-foreground block mb-1">{label.toUpperCase()}</label>
                    <input type={type} value={form[k]} onChange={set(k)} placeholder={label.replace(" *", "")}
                      className="w-full rounded-xl border border-border px-3 py-2 text-sm rounded-xl focus:outline-none focus:ring-1 focus:ring-[#0025D8] focus:border-[#0025D8]" />
                  </div>
                ))}
                <div>
                  <label className="font-mono text-[10px] tracking-[0.2em] text-muted-foreground block mb-1">NOTES (OPTIONAL)</label>
                  <textarea value={form.notes} onChange={set("notes")} rows={3} placeholder="Internal notes about this candidate…"
                    className="w-full rounded-xl border border-border px-3 py-2 text-sm rounded-xl focus:outline-none focus:ring-1 focus:ring-[#0025D8] focus:border-[#0025D8] resize-none" />
                </div>

                {/* Consent section — only when role has consent enabled */}
                {consentConfig?.consent_enabled && (
                  <div className="rounded-lg border border-[#0025D8]/20 bg-[#0025D8]/3 p-4 space-y-3">
                    <div className="font-mono text-[9px] tracking-[0.2em] text-[#0025D8] flex items-center gap-1.5">
                      <ShieldCheck className="w-3 h-3" />CONSENT — RECORDED BY COMPANY
                    </div>
                    <label className="flex items-start gap-3 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={consentGiven}
                        onChange={(e) => setConsentGiven(e.target.checked)}
                        className="mt-0.5 w-3.5 h-3.5 accent-[#0025D8] flex-shrink-0"
                      />
                      <span className="text-sm text-foreground leading-snug">
                        Candidate has given consent (collected via email or other offline method)
                      </span>
                    </label>
                    {consentGiven && (
                      <div className="space-y-3 pt-1">
                        {consentConfig.require_work_authorization && (
                          <div>
                            <label className="font-mono text-[10px] tracking-[0.15em] text-muted-foreground block mb-1">WORK AUTHORIZATION</label>
                            <select value={consentWorkAuth} onChange={(e) => setConsentWorkAuth(e.target.value)}
                              className="w-full rounded-xl border border-border px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#0025D8]">
                              <option value="">Select…</option>
                              {WORK_AUTH_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                            </select>
                          </div>
                        )}
                        {consentConfig.require_notice_period && (
                          <div>
                            <label className="font-mono text-[10px] tracking-[0.15em] text-muted-foreground block mb-1">NOTICE PERIOD (DAYS)</label>
                            <input type="number" min="0" max="365" value={consentNotice} onChange={(e) => setConsentNotice(e.target.value)}
                              placeholder="e.g. 14"
                              className="w-full rounded-xl border border-border px-3 py-2 text-sm focus:outline-none focus:border-[#0025D8]" />
                          </div>
                        )}
                        {consentConfig.require_earliest_start && (
                          <div>
                            <label className="font-mono text-[10px] tracking-[0.15em] text-muted-foreground block mb-1">EARLIEST START DATE</label>
                            <input type="date" value={consentStart} min={new Date().toISOString().slice(0, 10)}
                              onChange={(e) => setConsentStart(e.target.value)}
                              className="w-full rounded-xl border border-border px-3 py-2 text-sm focus:outline-none focus:border-[#0025D8]" />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {mode && (mode === "manual" || analyzed) && (
          <div className="p-5 border-t border-border flex items-center gap-3">
            <Button onClick={save} disabled={saving} className="rounded-xl bg-[#0025D8] hover:bg-[#001CB3]">
              {saving ? "Saving…" : "Save Candidate"}
            </Button>
            <button onClick={() => { setMode(null); setAnalyzed(null); setForm({ name: "", email: "", phone: "", location: "", linkedin_url: "", notes: "" }); }}
              className="text-sm text-muted-foreground hover:text-foreground font-mono">Back</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Confirm Dialog ────────────────────────────────────────────────────────────

function ConfirmDialog({ message, onConfirm, onCancel, confirmLabel = "Confirm", danger = false }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl border border-border p-6 max-w-sm w-full">
        <div className="flex items-start gap-3 mb-5">
          <AlertTriangle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${danger ? "text-[#E11D48]" : "text-[#F59E0B]"}`} />
          <p className="text-sm">{message}</p>
        </div>
        <div className="flex gap-3">
          <Button onClick={onConfirm} className={`rounded-xl ${danger ? "bg-[#E11D48] hover:bg-[#C81D3F]" : "bg-[#0025D8] hover:bg-[#001CB3]"}`}>{confirmLabel}</Button>
          <Button variant="outline" onClick={onCancel} className="rounded-xl">Cancel</Button>
        </div>
      </div>
    </div>
  );
}

// ── Hire Modal ────────────────────────────────────────────────────────────────

function HireModal({ candidate, onConfirm, onClose }) {
  const [offerDate, setOfferDate] = useState(new Date().toISOString().slice(0, 10));
  const [salary, setSalary] = useState("");
  const [saving, setSaving] = useState(false);

  const confirm = async () => {
    setSaving(true);
    try {
      await onConfirm({ offer_date: offerDate, salary_offered: salary });
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-xl border border-border w-full max-w-sm">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div>
            <div className="font-mono text-[10px] text-muted-foreground tracking-[0.2em]">MARK AS HIRED</div>
            <div className="font-display font-bold text-base mt-0.5">{candidate.name}</div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          {/* Score summary */}
          {(candidate.match_score != null || candidate.interview_score != null) && (
            <div className="flex items-center gap-6 rounded-xl border border-border p-3 bg-secondary/20">
              {candidate.match_score != null && (
                <div>
                  <div className="font-mono text-[9px] text-muted-foreground tracking-[0.15em] mb-0.5">MATCH SCORE</div>
                  <div className={`font-mono font-bold text-xl ${scoreColor(candidate.match_score)}`}>{Math.round(candidate.match_score)}%</div>
                </div>
              )}
              {candidate.interview_score != null && (
                <div>
                  <div className="font-mono text-[9px] text-muted-foreground tracking-[0.15em] mb-0.5">INTERVIEW SCORE</div>
                  <div className={`font-mono font-bold text-xl ${scoreColor(candidate.interview_score)}`}>{Math.round(candidate.interview_score)}%</div>
                </div>
              )}
            </div>
          )}
          <div>
            <label className="font-mono text-[10px] tracking-[0.2em] text-muted-foreground block mb-1.5">OFFER DATE</label>
            <input type="date" value={offerDate} onChange={(e) => setOfferDate(e.target.value)}
              className="w-full rounded-xl border border-border px-3 py-2 text-sm font-mono focus:outline-none focus:border-[#F59E0B] focus:ring-1 focus:ring-[#F59E0B]/20" />
          </div>
          <div>
            <label className="font-mono text-[10px] tracking-[0.2em] text-muted-foreground block mb-1.5">
              SALARY / COMPENSATION <span className="text-muted-foreground font-normal normal-case tracking-normal">(optional)</span>
            </label>
            <input type="text" value={salary} onChange={(e) => setSalary(e.target.value)}
              placeholder="e.g. $80,000 · £60k · $70k + equity"
              className="w-full rounded-xl border border-border px-3 py-2 text-sm font-mono focus:outline-none focus:border-[#F59E0B] focus:ring-1 focus:ring-[#F59E0B]/20" />
          </div>
        </div>
        <div className="p-5 border-t border-border flex items-center gap-3">
          <button onClick={confirm} disabled={saving || !offerDate}
            className="px-5 py-2.5 text-sm font-bold bg-[#F59E0B] text-white hover:bg-[#D97706] transition-colors disabled:opacity-60 flex items-center gap-2">
            <Check className="w-4 h-4" />
            {saving ? "Saving…" : "Confirm Hire"}
          </button>
          <button onClick={onClose} disabled={saving} className="text-sm text-muted-foreground hover:text-foreground font-mono">Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Pipeline Stats ─────────────────────────────────────────────────────────────

function PipelineStats({ candidates }) {
  const total = candidates.length;
  const shortlisted = candidates.filter((c) => ["shortlisted", "invited", "completed", "hired"].includes(c.status)).length;
  const interviewed = candidates.filter((c) => ["completed", "hired"].includes(c.status)).length;
  const hiredList = candidates.filter((c) => c.status === "hired");
  const hiredCount = hiredList.length;
  const hiredWithScore = hiredList.filter((c) => c.match_score != null);
  const avgMatch = hiredWithScore.length
    ? Math.round(hiredWithScore.reduce((s, c) => s + c.match_score, 0) / hiredWithScore.length)
    : null;

  const stats = [
    { label: "TOTAL", value: total, color: "text-foreground" },
    { label: "SHORTLISTED", value: shortlisted, color: "text-[#0025D8]" },
    { label: "INTERVIEWED", value: interviewed, color: "text-[#10B981]" },
    { label: "HIRED", value: hiredCount, color: hiredCount > 0 ? "text-[#B45309]" : "text-muted-foreground" },
    ...(avgMatch != null ? [{ label: "AVG MATCH (HIRED)", value: `${avgMatch}%`, color: scoreColor(avgMatch) }] : []),
  ];

  return (
    <div className="flex items-stretch rounded-xl border border-border bg-white mb-5 divide-x divide-border overflow-x-auto">
      {stats.map(({ label, value, color }) => (
        <div key={label} className="px-5 py-3 flex-shrink-0">
          <div className="font-mono text-[9px] tracking-[0.2em] text-muted-foreground mb-0.5">{label}</div>
          <div className={`font-mono font-bold text-xl ${color}`}>{value}</div>
        </div>
      ))}
    </div>
  );
}

// ── Candidates Tab ────────────────────────────────────────────────────────────

const SORT_COLS = [
  { key: "match_score", label: "Score" },
  { key: "name", label: "Name" },
  { key: "added_at", label: "Added" },
  { key: "status", label: "Status" },
];

// ── Interview Result Modal ─────────────────────────────────────────────────────

const CHEAT_TYPE_LABELS = {
  multiple_faces:  "Multiple People",
  face_missing:    "Face Not Visible",
  attention_drift: "Attention Drift",
  looking_away:    "Looking Away",
  looking_down:    "Eyes Looking Down",
  audio_coaching:  "Background Voice",
  tab_switch:      "Tab Switch",
  window_blur:     "Focus Lost",
  paste_detected:  "Paste Detected",
};

// ── Segment timeline viewer ─────────────────────────────────────────────────
function SegmentTimeline({ segments, cheatEvents, startedAt, activeIdx, onSegmentClick }) {
  const fmt = (secs) => {
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  // Map cheat event elapsed-seconds → segment index using started_at
  const flagMap = {}; // { segIndex: [eventLabel, ...] }
  if (startedAt) {
    const t0 = new Date(startedAt).getTime();
    cheatEvents.forEach(evt => {
      const elapsed = (new Date(evt.at).getTime() - t0) / 1000;
      const seg = segments.find(s => elapsed >= s.start_secs && elapsed < s.end_secs);
      if (seg) {
        if (!flagMap[seg.index]) flagMap[seg.index] = [];
        flagMap[seg.index].push(CHEAT_TYPE_LABELS[evt.type] || evt.type);
      }
    });
  }

  const activeSeg = segments.find(s => s.index === activeIdx);

  return (
    <div>
      {/* Segment grid */}
      <div className="flex flex-wrap gap-1.5 mb-3">
        {segments.map(seg => {
          const flags   = flagMap[seg.index] || [];
          const isActive = seg.index === activeIdx;
          return (
            <button
              key={seg.index}
              onClick={() => onSegmentClick(seg.index)}
              title={flags.length > 0 ? `⚠ ${flags.join(", ")}` : `${fmt(seg.start_secs)} – ${fmt(seg.end_secs)}`}
              className={`flex flex-col items-center px-2 py-1 border text-[10px] font-mono transition-colors ${
                isActive
                  ? "bg-[#0025D8] text-white border-[#0025D8]"
                  : flags.length > 0
                    ? "border-[#E11D48]/50 bg-[#E11D48]/5 text-[#E11D48] hover:bg-[#E11D48]/10"
                    : "border-border text-muted-foreground hover:bg-secondary/60"
              }`}
            >
              {flags.length > 0 && <span className="text-[8px] leading-none mb-0.5">⚠</span>}
              <span>{fmt(seg.start_secs)}</span>
            </button>
          );
        })}
      </div>

      {/* Active segment player */}
      {activeSeg && (
        <div>
          <div className="font-mono text-[10px] text-muted-foreground mb-1.5 flex items-center gap-2">
            <span>{fmt(activeSeg.start_secs)} – {fmt(activeSeg.end_secs)}</span>
            {flagMap[activeSeg.index]?.length > 0 && (
              <span className="text-[#E11D48]">⚠ {flagMap[activeSeg.index].join(", ")}</span>
            )}
          </div>
          <video
            key={activeSeg.url}
            src={activeSeg.url}
            controls
            autoPlay
            className="w-full rounded-xl border border-border bg-[#0F172A]"
            style={{ maxHeight: 260 }}
          />
        </div>
      )}
    </div>
  );
}

function InviteResultModal({ roleId, candidate, onClose }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading]           = useState(true);
  const [expandedFollowUps, setExpandedFollowUps] = useState({});
  const [expandedAttempts, setExpandedAttempts]   = useState({});
  // B1: presigned playback — either a single URL (legacy) or a segment manifest (new)
  const [videoPlaybackUrl, setVideoPlaybackUrl]   = useState(null);
  const [videoSegments, setVideoSegments]         = useState([]);
  const [activeSegmentIdx, setActiveSegmentIdx]   = useState(null);
  const [recordingStartedAt, setRecordingStartedAt] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/company/roles/${roleId}/candidates/${candidate.candidate_id}/invite-result`);
        setResult(data);
        // Fetch recording if any exists — new path returns segment manifest, old path returns single URL
        const hasRecording = data?.video_object_key || data?.video_segments?.length;
        if (hasRecording) {
          try {
            const { data: playback } = await api.get(
              `/company/roles/${roleId}/candidates/${candidate.candidate_id}/recording-view-url`
            );
            if (playback.type === "segments") {
              setVideoSegments(playback.segments);
              setRecordingStartedAt(playback.started_at);
              if (playback.segments.length > 0) setActiveSegmentIdx(playback.segments[0].index);
            } else {
              setVideoPlaybackUrl(playback.url);
            }
          } catch {} // non-fatal — video section just won't show
        }
      } catch { toast.error("Could not load interview result"); }
      finally { setLoading(false); }
    })();
  }, [roleId, candidate.candidate_id]);

  const evaluation  = result?.evaluation;
  const cheatEvents = result?.cheat_events || [];
  const answers     = result?.answers || [];
  const questions = result?.questions || [];

  // Build ordered question list from the questions array (which now comes from backend)
  const allEvalItems = [
    ...(evaluation?.ref_doc_eval?.items || []),
    ...(evaluation?.jd_eval?.items || []),
  ];

  // Build per-question display list: merge question text + answer + eval item
  const questionRows = questions
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((q, idx) => {
      const ans = answers.find((a) => a.q_id === q.id);
      const evalItem = allEvalItems.find((e) => e.q_id === q.id);
      return { q, ans, evalItem, idx };
    });

  const toggleFollowUps = (qId) => setExpandedFollowUps((p) => ({ ...p, [qId]: !p[qId] }));
  const toggleAttempts = (qId) => setExpandedAttempts((p) => ({ ...p, [qId]: !p[qId] }));

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-xl border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border sticky top-0 bg-white z-10">
          <div>
            <div className="font-mono text-[10px] text-muted-foreground tracking-[0.2em]">INTERVIEW RESULT</div>
            <div className="font-display font-bold text-lg mt-0.5">{candidate.name}</div>
            {result?.completed_at && (
              <div className="font-mono text-[10px] text-muted-foreground mt-0.5">
                Completed {new Date(result.completed_at).toLocaleString()}
              </div>
            )}
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
        </div>

        {loading ? (
          <div className="p-12 text-center font-mono text-sm text-muted-foreground">Loading…</div>
        ) : !result ? (
          <div className="p-8 text-center text-sm text-muted-foreground">No result available.</div>
        ) : (
          <div className="p-5 space-y-5">

            {/* Score + recommendation */}
            {evaluation && (
              <div className={`border p-4 ${scoreBg(evaluation.overall_score)}`}>
                <div className="flex items-center gap-4 mb-2">
                  <div>
                    <div className="font-mono text-[10px] tracking-[0.2em] mb-0.5">OVERALL SCORE</div>
                    <div className="font-display font-bold text-3xl">
                      {evaluation.overall_score != null ? `${evaluation.overall_score}%` : "—"}
                    </div>
                  </div>
                  {evaluation.recommendation && (
                    <div>
                      <div className="font-mono text-[10px] tracking-[0.2em] text-muted-foreground mb-1">RECOMMENDATION</div>
                      <div className="font-semibold">{evaluation.recommendation}</div>
                    </div>
                  )}
                </div>
                {evaluation.summary && (
                  <p className="text-xs text-muted-foreground leading-relaxed">{evaluation.summary}</p>
                )}
              </div>
            )}

            {/* Session recording */}
            {videoSegments.length > 0 && (
              <div>
                <div className="font-mono text-[10px] tracking-[0.2em] text-muted-foreground mb-2 flex items-center gap-2">
                  SESSION RECORDING
                  <span className="px-1.5 py-0.5 rounded-xl border border-border text-[9px]">
                    {videoSegments.length} clip{videoSegments.length !== 1 ? "s" : ""}
                  </span>
                </div>
                <SegmentTimeline
                  segments={videoSegments}
                  cheatEvents={cheatEvents}
                  startedAt={recordingStartedAt}
                  activeIdx={activeSegmentIdx}
                  onSegmentClick={setActiveSegmentIdx}
                />
              </div>
            )}
            {!videoSegments.length && videoPlaybackUrl && (
              <div>
                <div className="font-mono text-[10px] tracking-[0.2em] text-muted-foreground mb-2">
                  SESSION RECORDING
                </div>
                <video
                  src={videoPlaybackUrl}
                  controls
                  className="w-full rounded-xl border border-border bg-[#0F172A]"
                  style={{ maxHeight: 280 }}
                />
              </div>
            )}

            {/* Proctoring log */}
            <div>
              <div className="font-mono text-[10px] tracking-[0.2em] text-muted-foreground mb-2 flex items-center gap-2">
                PROCTORING LOG
                <span className={`px-1.5 py-0.5 border text-[9px] font-bold ${
                  cheatEvents.length > 0
                    ? "bg-[#E11D48]/10 text-[#E11D48] border-[#E11D48]/30"
                    : "bg-[#10B981]/10 text-[#10B981] border-[#10B981]/30"
                }`}>
                  {cheatEvents.length} event{cheatEvents.length !== 1 ? "s" : ""}
                </span>
              </div>
              {cheatEvents.length === 0 ? (
                <div className="rounded-lg border border-[#10B981]/30 bg-[#10B981]/5 px-4 py-3 font-mono text-xs text-[#10B981]">
                  No proctoring violations detected.
                </div>
              ) : (
                <div className="rounded-lg border border-[#E11D48]/20 divide-y divide-[#E11D48]/10">
                  {cheatEvents.map((evt, i) => {
                    // Find matching segment so company can jump directly to it
                    let jumpSegIdx = null;
                    if (recordingStartedAt && videoSegments.length > 0) {
                      const elapsed = (new Date(evt.at).getTime() - new Date(recordingStartedAt).getTime()) / 1000;
                      const match = videoSegments.find(s => elapsed >= s.start_secs && elapsed < s.end_secs);
                      if (match) jumpSegIdx = match.index;
                    }
                    return (
                      <div key={i} className="flex items-center gap-3 px-4 py-2.5">
                        <AlertTriangle className="w-3.5 h-3.5 text-[#E11D48] flex-shrink-0" />
                        <span className="font-mono text-xs font-bold text-[#E11D48] w-36 flex-shrink-0">
                          {CHEAT_TYPE_LABELS[evt.type] || evt.type}
                        </span>
                        <span className="font-mono text-[10px] text-muted-foreground flex-1">
                          {new Date(evt.at).toLocaleTimeString()}
                          {evt.duration_sec > 0 && ` · ${evt.duration_sec}s`}
                        </span>
                        {jumpSegIdx !== null && (
                          <button
                            onClick={() => setActiveSegmentIdx(jumpSegIdx)}
                            className="flex-shrink-0 font-mono text-[9px] px-1.5 py-0.5 rounded-lg border border-[#E11D48]/40 text-[#E11D48] hover:bg-[#E11D48]/10 transition-colors"
                          >
                            View clip →
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Per-question breakdown */}
            {questionRows.length > 0 && (
              <div>
                <div className="font-mono text-[10px] tracking-[0.2em] text-muted-foreground mb-3">
                  QUESTION BREAKDOWN
                </div>
                <div className="space-y-4">
                  {questionRows.map(({ q, ans, evalItem, idx }) => {
                    const score = evalItem?.score ?? evalItem?.intent_score;
                    const retakeCount = ans?.retake_count || 0;
                    const method = ans?.answer_method || "spoken";
                    const isTyped = method === "typed";
                    const methodLabel = isTyped ? "Typed" : retakeCount > 0 ? `Rerecorded ×${retakeCount}` : "Spoken";
                    const methodStyle = isTyped || retakeCount > 0
                      ? "bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/30"
                      : "bg-[#10B981]/10 text-[#10B981] border-[#10B981]/30";
                    const followUps = ans?.follow_up_conversation || [];
                    const prevAttempts = ans?.previous_attempts || [];
                    const fuOpen = !!expandedFollowUps[q.id];
                    const attOpen = !!expandedAttempts[q.id];

                    return (
                      <div key={q.id} className="rounded-xl border border-border">
                        {/* Question header */}
                        <div className="flex items-start justify-between gap-3 px-4 py-3 bg-secondary/30 border-b border-border">
                          <div className="flex items-start gap-2 flex-1 min-w-0">
                            <span className="font-mono text-[10px] text-muted-foreground flex-shrink-0 mt-0.5">Q{idx + 1}</span>
                            <p className="text-sm font-semibold leading-snug">{q.text}</p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className={`font-mono text-[9px] px-1.5 py-0.5 border ${methodStyle}`}>{methodLabel}</span>
                            {score != null && (
                              <span className={`font-mono font-bold text-base ${scoreColor(score)}`}>{score}%</span>
                            )}
                          </div>
                        </div>

                        <div className="px-4 py-3 space-y-3">
                          {/* AI feedback */}
                          {evalItem?.feedback && (
                            <p className="text-xs text-muted-foreground leading-relaxed border-l-2 border-[#0025D8]/30 pl-3">
                              {evalItem.feedback}
                            </p>
                          )}

                          {/* Full answer transcript */}
                          {ans?.text ? (
                            <div>
                              <div className="font-mono text-[9px] text-muted-foreground tracking-[0.15em] mb-1.5">ANSWER TRANSCRIPT</div>
                              <p className="text-sm leading-relaxed text-foreground whitespace-pre-wrap">{ans.text}</p>
                            </div>
                          ) : (
                            <p className="text-xs text-muted-foreground italic">No answer recorded.</p>
                          )}

                          {/* Follow-up conversation thread */}
                          {followUps.length > 0 && (
                            <div>
                              <button
                                onClick={() => toggleFollowUps(q.id)}
                                className="flex items-center gap-1.5 font-mono text-[10px] text-[#0025D8] hover:text-[#001CB3] transition-colors"
                              >
                                <ChevronDown className={`w-3 h-3 transition-transform ${fuOpen ? "rotate-180" : ""}`} />
                                {fuOpen ? "Hide" : "Show"} follow-up conversation ({Math.ceil(followUps.filter(t => t.role === "candidate").length)} answer{followUps.filter(t => t.role === "candidate").length !== 1 ? "s" : ""})
                              </button>
                              {fuOpen && (
                                <div className="mt-2 space-y-2 border-l-2 border-[#0025D8]/20 pl-3">
                                  {followUps.map((turn, ti) => (
                                    <div key={ti}>
                                      <div className={`font-mono text-[9px] tracking-[0.15em] mb-0.5 ${turn.role === "interviewer" ? "text-[#0025D8]" : "text-muted-foreground"}`}>
                                        {turn.role === "interviewer" ? "AI FOLLOW-UP" : "CANDIDATE"}
                                      </div>
                                      <p className="text-xs leading-relaxed">{turn.text}</p>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Previous attempts (retakes) */}
                          {prevAttempts.length > 0 && (
                            <div>
                              <button
                                onClick={() => toggleAttempts(q.id)}
                                className="flex items-center gap-1.5 font-mono text-[10px] text-[#F59E0B] hover:text-[#D97706] transition-colors"
                              >
                                <ChevronDown className={`w-3 h-3 transition-transform ${attOpen ? "rotate-180" : ""}`} />
                                {attOpen ? "Hide" : "Show"} {prevAttempts.length} discarded attempt{prevAttempts.length !== 1 ? "s" : ""}
                              </button>
                              {attOpen && (
                                <div className="mt-2 space-y-3">
                                  {prevAttempts.map((attempt, ai) => (
                                    <div key={ai} className="rounded-lg border border-[#F59E0B]/20 bg-[#F59E0B]/5 px-3 py-2">
                                      <div className="font-mono text-[9px] text-[#F59E0B] tracking-[0.15em] mb-1">ATTEMPT {ai + 1} (DISCARDED)</div>
                                      <p className="text-xs leading-relaxed text-muted-foreground">{attempt.answer || <em>No transcript</em>}</p>
                                      {attempt.follow_up_conversation?.length > 0 && (
                                        <div className="mt-2 space-y-1.5 border-l-2 border-[#F59E0B]/30 pl-2">
                                          {attempt.follow_up_conversation.map((t, ti) => (
                                            <p key={ti} className="text-[11px] text-muted-foreground">
                                              <span className="font-mono text-[9px] mr-1">{t.role === "interviewer" ? "AI:" : "Candidate:"}</span>
                                              {t.text}
                                            </p>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Send Invite Preview Modal ─────────────────────────────────────────────────

function SendInvitePreviewModal({ role, candidates, setupData, sending, sendProgress, onSend, onClose }) {
  const { user } = useAuth();
  // Admins can buy credits inline; employees are asked to contact an admin. This was referenced
  // in the over-capacity branch below WITHOUT being defined here — a ReferenceError that crashed
  // the whole modal (so it never appeared) whenever a batch exceeded the plan's interview capacity.
  const isAdmin = user?.role === "company" && user?.sub_role !== "employee";
  const shortlisted = candidates.filter((c) => c.shortlisted && c.status !== "invited");
  const questions = (setupData?.questions || []).filter((q) => q.selected && q.text?.trim());
  const expiresDays = setupData?.invite_expires_days ?? null;
  const retakes = setupData?.retakes_per_question ?? 1;

  // Interview capacity gate: allowance remaining this cycle + carry-over credits.
  // Sending is all-or-nothing — if capacity can't cover every shortlisted candidate,
  // the admin must buy the shortfall (instant, card on file) before sending.
  const billing = user?.company_billing || {};
  const ivLimit = billing.interview_limit;                 // null = unlimited
  const unlimitedIv = ivLimit == null;
  const remainingAllowance = unlimitedIv ? Infinity : Math.max(0, ivLimit - (billing.interviews_used || 0));
  const credits = billing.interview_credits || 0;
  const capacity = unlimitedIv ? Infinity : remainingAllowance + credits;
  const needed = shortlisted.length;
  const shortfall = unlimitedIv ? 0 : Math.max(0, needed - capacity);
  const canSend = unlimitedIv || capacity >= needed;

  const expiryLabel = expiresDays == null ? "No Expiry" : expiresDays === 1 ? "1 Day" : `${expiresDays} Days`;
  const retakesLabel = retakes === 0 ? "0 — Strict" : retakes === 1 ? "1 Retake" : "2 Retakes";

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-xl border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border sticky top-0 bg-white z-10">
          <div>
            <div className="font-mono text-[10px] text-muted-foreground tracking-[0.2em]">CONFIRM & SEND</div>
            <div className="font-display font-bold text-lg mt-0.5">Send Interview Invites</div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-5 flex-1">
          {/* Section 0 — Interview capacity (all-or-nothing gate) */}
          {!unlimitedIv && (
            <div className={`rounded-xl border px-4 py-3.5 ${canSend ? "border-border bg-secondary/30" : "border-[#E11D48]/40 bg-[#E11D48]/5"}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] text-muted-foreground">
                  <Mic className="w-3 h-3" /> INTERVIEW CAPACITY
                </div>
                <div className="text-sm">
                  <span className={`font-mono font-bold ${canSend ? "text-[#0F172A]" : "text-[#E11D48]"}`}>
                    {capacity} available
                  </span>
                  <span className="text-muted-foreground"> · sending {needed}</span>
                </div>
              </div>
              <p className="font-mono text-[10px] text-muted-foreground mt-1.5">
                {remainingAllowance} left this cycle{credits ? ` + ${credits} carry-over credit${credits !== 1 ? "s" : ""}` : ""}
              </p>
              {!canSend && (
                <div className="flex items-center justify-between gap-3 flex-wrap mt-3 pt-3 border-t border-[#E11D48]/20">
                  {isAdmin ? (
                    <>
                      <p className="text-xs font-semibold text-[#E11D48]">
                        Not enough credits — buy at least {shortfall} more to send all {needed} invites.
                      </p>
                      <TopUpButton
                        recommend={shortfall}
                        label="Buy credits"
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-[#0025D8] hover:bg-[#001CB3] text-white transition-colors disabled:opacity-60" />
                    </>
                  ) : (
                    <p className="text-xs font-semibold text-[#E11D48]">
                      Not enough interview credits — ask your account admin to buy at least {shortfall} more to send all {needed} invites.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Section 1 — Interview Settings */}
          <div>
            <div className="font-mono text-[10px] tracking-[0.2em] text-muted-foreground mb-3 flex items-center gap-2">
              <Settings className="w-3 h-3" /> INTERVIEW SETTINGS
            </div>
            <div className="rounded-xl border border-border divide-y divide-border">
              <div className="flex items-center gap-4 px-4 py-3">
                <span className="font-mono text-[10px] text-muted-foreground tracking-[0.15em] w-36 flex-shrink-0">QUESTIONS</span>
                <span className="font-mono text-sm font-bold text-[#0025D8]">{questions.length} selected</span>
              </div>
              <div className="flex items-center gap-4 px-4 py-3">
                <span className="font-mono text-[10px] text-muted-foreground tracking-[0.15em] w-36 flex-shrink-0">LINK EXPIRES</span>
                <span className="font-mono text-sm font-semibold">{expiryLabel}</span>
              </div>
              <div className="flex items-center gap-4 px-4 py-3">
                <span className="font-mono text-[10px] text-muted-foreground tracking-[0.15em] w-36 flex-shrink-0">RETAKES</span>
                <span className="font-mono text-sm font-semibold">{retakesLabel}</span>
              </div>
            </div>
          </div>

          {/* Section 2 — Candidates */}
          <div>
            <div className="font-mono text-[10px] tracking-[0.2em] text-muted-foreground mb-3 flex items-center gap-2">
              <Users className="w-3 h-3" /> CANDIDATES ({shortlisted.length})
            </div>
            <div className="rounded-xl border border-border divide-y divide-border">
              {shortlisted.map((c) => (
                <div key={c.candidate_id} className="flex items-center gap-3 px-4 py-2.5">
                  <div className="w-6 h-6 bg-[#0025D8]/10 text-[#0025D8] font-mono text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                    {c.name?.[0]?.toUpperCase() || "?"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{c.name}</div>
                    <div className="text-xs text-muted-foreground truncate">{c.email}</div>
                  </div>
                  {c.match_score != null && (
                    <span className={`font-mono text-xs font-bold flex-shrink-0 ${scoreColor(c.match_score)}`}>
                      {Math.round(c.match_score)}%
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Section 3 — Questions */}
          <div>
            <div className="font-mono text-[10px] tracking-[0.2em] text-muted-foreground mb-3 flex items-center gap-2">
              <FileText className="w-3 h-3" /> QUESTIONS ({questions.length})
            </div>
            <div className="rounded-xl border border-border divide-y divide-border">
              {questions.map((q, i) => (
                <div key={q.id} className="flex items-start gap-3 px-4 py-3">
                  <span className="font-mono text-[10px] text-muted-foreground flex-shrink-0 w-5 pt-0.5">{i + 1}.</span>
                  <p className="text-sm leading-relaxed">{q.text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Section 4 — Email Preview */}
          <div>
            <div className="font-mono text-[10px] tracking-[0.2em] text-muted-foreground mb-3 flex items-center gap-2">
              <Send className="w-3 h-3" /> EMAIL PREVIEW
            </div>
            <div className="rounded-xl border border-border bg-secondary/30">
              <div className="border-b border-border px-4 py-3 space-y-1">
                <div className="flex items-center gap-2 text-xs">
                  <span className="font-mono text-muted-foreground w-12 flex-shrink-0">From:</span>
                  <span className="font-semibold">VetraHub</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <span className="font-mono text-muted-foreground w-12 flex-shrink-0">To:</span>
                  <span className="text-muted-foreground">{shortlisted.slice(0, 2).map((c) => c.email).join(", ")}{shortlisted.length > 2 ? ` +${shortlisted.length - 2} more` : ""}</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <span className="font-mono text-muted-foreground w-12 flex-shrink-0">Subject:</span>
                  <span className="font-semibold">Interview Invitation — {role.title}</span>
                </div>
              </div>
              <div className="px-4 py-4 text-sm text-muted-foreground leading-relaxed space-y-3">
                <p>Hi [Candidate Name],</p>
                <p>
                  You've been invited to complete a short AI-powered video interview for the{" "}
                  <span className="font-semibold text-foreground">{role.title}</span> position.
                </p>
                <p>
                  The interview contains <span className="font-semibold text-foreground">{questions.length} question{questions.length !== 1 ? "s" : ""}</span>.{" "}
                  {retakes > 0 && <>You may re-record each answer up to {retakes === 1 ? "once" : "twice"}. </>}
                  {expiresDays != null && <>Your invite link expires in {expiresDays} day{expiresDays !== 1 ? "s" : ""}. </>}
                </p>
                <div className="inline-block bg-[#0025D8] text-white text-xs font-bold px-4 py-2 font-mono tracking-wider">
                  START INTERVIEW →
                </div>
                <p className="text-xs">Good luck!</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-border sticky bottom-0 bg-white">
          <div className="flex items-center gap-3">
            <Button
              onClick={onSend}
              disabled={sending || shortlisted.length === 0 || !canSend}
              title={!canSend ? "Buy interview credits to send all invites" : undefined}
              className="rounded-xl bg-[#10B981] hover:bg-[#059669] text-white flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
              <Send className="w-4 h-4" />
              {sending ? "Sending…" : `Send ${shortlisted.length} Invite${shortlisted.length !== 1 ? "s" : ""}`}
            </Button>
            <Button variant="outline" onClick={onClose} disabled={sending} className="rounded-xl">
              Cancel
            </Button>
          </div>
          {sendProgress?.isRunning && (
            <ProgressSteps steps={SEND_INVITES_STEPS} currentStep={sendProgress.currentStep} pct={sendProgress.pct} />
          )}
        </div>
      </div>
    </div>
  );
}

// ── Resend Invite Modal ───────────────────────────────────────────────────────

function ResendInviteModal({ roleId, candidate, onClose, onResent }) {
  const [email, setEmail] = useState(candidate.email);
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    const trimmed = email.trim();
    if (!trimmed) { toast.error("Email is required"); return; }
    setSending(true);
    try {
      await api.post(`/company/roles/${roleId}/candidates/${candidate.candidate_id}/resend-invite`, { email: trimmed });
      toast.success(`Invite resent to ${trimmed}`);
      onResent(trimmed);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not resend invite — try again");
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-xl border border-border w-full max-w-sm">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div>
            <div className="font-mono text-[10px] text-muted-foreground tracking-[0.2em]">RESEND INVITE</div>
            <div className="font-display font-bold text-base mt-0.5">{candidate.name}</div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-muted-foreground">A new unique link will be generated and emailed. The previous link will stop working.</p>
          <div>
            <label className="font-mono text-[10px] tracking-[0.2em] text-muted-foreground block mb-1.5">SEND TO EMAIL</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-xl border border-border px-3 py-2 text-sm font-mono focus:outline-none focus:border-[#0025D8]"
              placeholder="candidate@email.com"
            />
          </div>
        </div>
        <div className="p-5 border-t border-border flex items-center gap-2 justify-end">
          <button onClick={onClose} disabled={sending}
            className="px-4 py-2 text-sm rounded-xl border border-border text-muted-foreground hover:text-foreground transition-colors">
            Cancel
          </button>
          <button onClick={handleSend} disabled={sending}
            className="px-4 py-2 text-sm font-bold rounded-lg bg-[#0025D8] text-white hover:bg-[#001CB3] transition-colors disabled:opacity-60 flex items-center gap-2">
            <RotateCcw className="w-3.5 h-3.5" />
            {sending ? "Sending…" : "Resend Invite"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ApplyLinkBox({ roleId, roleStatus }) {
  const [copied, setCopied] = useState(false);
  const applyUrl = `${window.location.origin}${process.env.PUBLIC_URL || ""}/apply/${roleId}`;
  const isActive = roleStatus === "active";

  const copy = () => {
    navigator.clipboard.writeText(applyUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className={`mb-5 border p-4 flex items-center gap-3 flex-wrap ${isActive ? "border-[#0025D8]/20 bg-[#0025D8]/3" : "border-border bg-secondary/30"}`}>
      <Link2 className={`w-4 h-4 flex-shrink-0 ${isActive ? "text-[#0025D8]" : "text-muted-foreground"}`} />
      <div className="flex-1 min-w-0">
        <div className="font-mono text-[10px] tracking-[0.15em] text-muted-foreground mb-0.5">CANDIDATE APPLY LINK</div>
        <div className="font-mono text-xs text-foreground truncate">{applyUrl}</div>
      </div>
      {isActive ? (
        <button
          onClick={copy}
          className={`flex items-center gap-1.5 px-3 py-1.5 font-mono text-[10px] font-bold border transition-all flex-shrink-0 ${
            copied
              ? "bg-[#10B981] text-white border-[#10B981]"
              : "border-[#0025D8]/40 text-[#0025D8] hover:bg-[#0025D8] hover:text-white"
          }`}
        >
          {copied ? <><Check className="w-3 h-3" /> COPIED</> : <><Copy className="w-3 h-3" /> COPY</>}
        </button>
      ) : (
        <span className="font-mono text-[10px] text-muted-foreground flex-shrink-0">Role is {roleStatus} — link inactive</span>
      )}
    </div>
  );
}

// ── Candidate Filters ─────────────────────────────────────────────────────────

const WORK_AUTH_OPTIONS = [
  "US Citizen / Permanent Resident", "Green Card Holder", "H-1B Visa",
  "OPT / CPT", "TN Visa", "Requires Sponsorship",
  "Canada PR / Citizen", "LMIA Required", "Not Applicable / Other",
];

function CandidateFilters({ filters, onChange, total, filtered, onReset }) {
  const [open, setOpen] = useState(false);
  const activeCount = [
    filters.search, filters.status, filters.source,
    filters.consent, filters.workAuth,
  ].filter(Boolean).length;

  return (
    <div className="mb-4 space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        {/* Search */}
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
          <input
            type="text"
            value={filters.search}
            onChange={(e) => onChange({ ...filters, search: e.target.value })}
            placeholder="Search name or email…"
            className="w-full rounded-xl border border-border pl-8 pr-3 py-2 text-sm focus:outline-none focus:border-[#0025D8] focus:ring-1 focus:ring-[#0025D8]/20 transition-all"
          />
        </div>

        {/* Filters toggle */}
        <button
          onClick={() => setOpen((o) => !o)}
          className={`flex items-center gap-2 px-3 py-2 border text-sm font-mono text-[10px] tracking-[0.1em] transition-colors ${open || activeCount > 0 ? "border-[#0025D8] text-[#0025D8] bg-[#0025D8]/5" : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/30"}`}
        >
          <SlidersHorizontal className="w-3.5 h-3.5" />
          FILTERS
          {activeCount > 0 && (
            <span className="w-4 h-4 rounded-full bg-[#0025D8] text-white text-[9px] font-bold flex items-center justify-center">{activeCount}</span>
          )}
        </button>

        {/* Result count */}
        <span className="font-mono text-[10px] text-muted-foreground ml-auto">
          {filtered < total ? `${filtered} of ${total}` : `${total}`} candidate{total !== 1 ? "s" : ""}
        </span>

        {/* Reset */}
        {activeCount > 0 && (
          <button onClick={onReset} className="font-mono text-[10px] text-muted-foreground hover:text-[#E11D48] transition-colors underline">
            Clear filters
          </button>
        )}
      </div>

      {open && (
        <div className="rounded-xl border border-border bg-secondary/10 p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          {/* Status */}
          <div>
            <div className="font-mono text-[9px] tracking-[0.2em] text-muted-foreground mb-1.5">STATUS</div>
            <select value={filters.status} onChange={(e) => onChange({ ...filters, status: e.target.value })}
              className="w-full rounded-xl border border-border px-2 py-1.5 text-sm bg-white focus:outline-none focus:border-[#0025D8]">
              <option value="">All</option>
              <option value="under_review">Under Review</option>
              <option value="shortlisted">Shortlisted</option>
              <option value="invited">Invited</option>
              <option value="completed">Completed</option>
              <option value="hired">Hired</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          {/* Source */}
          <div>
            <div className="font-mono text-[9px] tracking-[0.2em] text-muted-foreground mb-1.5">SOURCE</div>
            <select value={filters.source} onChange={(e) => onChange({ ...filters, source: e.target.value })}
              className="w-full rounded-xl border border-border px-2 py-1.5 text-sm bg-white focus:outline-none focus:border-[#0025D8]">
              <option value="">All Sources</option>
              <option value="manual">Manual</option>
              <option value="apply_link">Via Apply Link</option>
            </select>
          </div>

          {/* Consent */}
          <div>
            <div className="font-mono text-[9px] tracking-[0.2em] text-muted-foreground mb-1.5">CONSENT</div>
            <select value={filters.consent} onChange={(e) => onChange({ ...filters, consent: e.target.value })}
              className="w-full rounded-xl border border-border px-2 py-1.5 text-sm bg-white focus:outline-none focus:border-[#0025D8]">
              <option value="">All</option>
              <option value="consented">Consented</option>
              <option value="not_required">Not Required</option>
              <option value="none">No Consent Data</option>
            </select>
          </div>

          {/* Work Auth */}
          <div>
            <div className="font-mono text-[9px] tracking-[0.2em] text-muted-foreground mb-1.5">WORK AUTH</div>
            <select value={filters.workAuth} onChange={(e) => onChange({ ...filters, workAuth: e.target.value })}
              className="w-full rounded-xl border border-border px-2 py-1.5 text-sm bg-white focus:outline-none focus:border-[#0025D8]">
              <option value="">All</option>
              {WORK_AUTH_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}

function CopyToRoleModal({ candidate, sourceRoleId, onClose, onCopied }) {
  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [search, setSearch] = useState("");
  const [copying, setCopying] = useState(false);
  const [results, setResults] = useState(null); // null = selection phase, array = results phase

  useEffect(() => {
    api.get("/company/roles/mine")
      .then(({ data }) => setRoles((Array.isArray(data) ? data : data.roles || []).filter((r) => r.company_role_id !== sourceRoleId && r.status !== "closed")))
      .catch(() => toast.error("Could not load roles"))
      .finally(() => setLoadingRoles(false));
  }, [sourceRoleId]);

  const filtered = roles.filter((r) => r.title.toLowerCase().includes(search.toLowerCase()));
  const allVisibleSelected = filtered.length > 0 && filtered.every((r) => selectedIds.has(r.company_role_id));

  const toggle = (id) => setSelectedIds((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const toggleAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        filtered.forEach((r) => next.delete(r.company_role_id));
      } else {
        filtered.forEach((r) => next.add(r.company_role_id));
      }
      return next;
    });
  };

  const handleCopy = async () => {
    if (selectedIds.size === 0) return;
    setCopying(true);
    const targets = roles.filter((r) => selectedIds.has(r.company_role_id));
    const settled = await Promise.allSettled(
      targets.map((r) =>
        api.post(`/company/roles/${r.company_role_id}/candidates/copy-from`, {
          source_candidate_id: candidate.candidate_id,
          source_role_id: sourceRoleId,
        }).then(() => ({ role: r, status: "copied" }))
          .catch((err) => ({ role: r, status: "failed", reason: err?.response?.data?.detail || "Error" }))
      )
    );
    const outcomes = settled.map((s) => s.value);
    setResults(outcomes);
    const copied = outcomes.filter((o) => o.status === "copied").length;
    if (copied > 0) onCopied?.();
    setCopying(false);
  };

  // ── Results screen ──
  if (results) {
    const copied = results.filter((o) => o.status === "copied");
    const failed = results.filter((o) => o.status === "failed");
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl border border-border w-full max-w-sm">
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <div>
              <div className="font-mono text-[10px] tracking-[0.2em] text-muted-foreground mb-0.5">COPY COMPLETE</div>
              <p className="font-semibold text-sm">{candidate.name}</p>
            </div>
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="p-5 space-y-2">
            <div className="flex items-center gap-4 mb-3">
              <span className="font-mono text-[10px] text-[#10B981]">{copied.length} COPIED</span>
              {failed.length > 0 && <span className="font-mono text-[10px] text-[#E11D48]">{failed.length} SKIPPED</span>}
            </div>
            <div className="space-y-1.5 max-h-64 overflow-y-auto">
              {results.map(({ role: r, status, reason }) => (
                <div key={r.company_role_id} className={`flex items-start gap-2.5 px-3 py-2.5 border ${
                  status === "copied" ? "border-[#10B981]/30 bg-[#10B981]/5" : "border-[#E11D48]/20 bg-[#E11D48]/5"
                }`}>
                  {status === "copied"
                    ? <Check className="w-3.5 h-3.5 text-[#10B981] flex-shrink-0 mt-0.5" />
                    : <AlertTriangle className="w-3.5 h-3.5 text-[#E11D48] flex-shrink-0 mt-0.5" />
                  }
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{r.title}</div>
                    {status === "failed" && (
                      <div className="font-mono text-[10px] text-[#E11D48] mt-0.5">{reason}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end px-5 py-4 border-t border-border">
            <button onClick={onClose}
              className="px-5 py-2 text-sm font-bold bg-[#0025D8] text-white rounded-lg border border-[#0025D8] hover:bg-[#001CB3] transition-all">
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Selection screen ──
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl border border-border w-full max-w-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div>
            <div className="font-mono text-[10px] tracking-[0.2em] text-muted-foreground mb-0.5">COPY CANDIDATE</div>
            <p className="font-semibold text-sm">{candidate.name}</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-3">
          <p className="text-xs text-muted-foreground">Select one or more roles to copy this candidate into. Profile and resume carry over; AI match score is recalculated per role.</p>

          <div className="flex items-center gap-2">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search roles…"
              className="flex-1 rounded-xl border border-border px-3 py-2 text-sm focus:outline-none focus:border-[#0025D8] focus:ring-1 focus:ring-[#0025D8]/20"
            />
            {filtered.length > 1 && (
              <button type="button" onClick={toggleAllVisible}
                className="font-mono text-[9px] px-2.5 py-2 rounded-xl border border-border text-muted-foreground hover:border-[#0025D8] hover:text-[#0025D8] transition-all whitespace-nowrap">
                {allVisibleSelected ? "Deselect all" : "Select all"}
              </button>
            )}
          </div>

          <div className="space-y-1 max-h-52 overflow-y-auto">
            {loadingRoles ? (
              <div className="py-6 text-center font-mono text-[10px] text-muted-foreground">Loading roles…</div>
            ) : filtered.length === 0 ? (
              <div className="py-6 text-center font-mono text-[10px] text-muted-foreground">
                {roles.length === 0 ? "No other active roles found" : "No roles match your search"}
              </div>
            ) : filtered.map((r) => {
              const checked = selectedIds.has(r.company_role_id);
              return (
                <label key={r.company_role_id}
                  className={`flex items-center gap-3 px-3 py-2.5 border cursor-pointer transition-all ${
                    checked ? "border-[#0025D8] bg-[#0025D8]/5" : "border-border hover:border-[#0025D8]/40 hover:bg-secondary/30"
                  }`}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(r.company_role_id)}
                    className="w-3.5 h-3.5 accent-[#0025D8] flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className={`text-sm font-medium truncate ${checked ? "text-[#0025D8]" : ""}`}>{r.title}</span>
                      <span className={`font-mono text-[9px] px-1.5 py-0.5 border flex-shrink-0 ${
                        r.status === "active" ? "text-[#10B981] border-[#10B981]/30 bg-[#10B981]/8"
                        : "text-muted-foreground border-border bg-secondary/40"
                      }`}>{r.status.toUpperCase()}</span>
                    </div>
                    {r.location && <div className="font-mono text-[9px] text-muted-foreground mt-0.5">{r.location}</div>}
                  </div>
                </label>
              );
            })}
          </div>
        </div>

        <div className="flex items-center justify-between px-5 py-4 border-t border-border gap-3">
          <span className="font-mono text-[10px] text-muted-foreground">
            {selectedIds.size > 0 ? `${selectedIds.size} role${selectedIds.size !== 1 ? "s" : ""} selected` : "No roles selected"}
          </span>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm rounded-xl border border-border text-muted-foreground hover:text-foreground transition-colors">
              Cancel
            </button>
            <button
              onClick={handleCopy}
              disabled={selectedIds.size === 0 || copying}
              className="flex items-center gap-2 px-5 py-2 text-sm font-bold bg-[#0025D8] text-white rounded-lg border border-[#0025D8] hover:bg-[#001CB3] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Copy className="w-3.5 h-3.5" />
              {copying ? "Copying…" : `Copy to ${selectedIds.size || ""} Role${selectedIds.size !== 1 ? "s" : ""}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CandidatesTab({ role, tab }) {
  const { isLocked } = useCompanyGated();
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("match_score");
  const [sortDir, setSortDir] = useState("desc");
  const [showAdd, setShowAdd] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState(null); // full candidate object
  const [sendConfirm, setSendConfirm] = useState(false);
  const [sending, setSending] = useState(false);
  const sendInvitesProgress = useProgressSteps(SEND_INVITES_STEPS);
  const [editId, setEditId] = useState(null);
  const [resendTarget, setResendTarget] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "", phone: "" });
  const [editSaving, setEditSaving] = useState(false);
  const [hasQuestions, setHasQuestions] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [viewResult, setViewResult] = useState(null);
  const [filters, setFilters] = useState({ search: "", status: "", source: "", consent: "", workAuth: "" });
  const [hireTarget, setHireTarget] = useState(null);
  const [roleConsentConfig, setRoleConsentConfig] = useState(null);
  const [editConsentForm, setEditConsentForm] = useState({ consent_given: false, work_authorization: "", notice_period_days: "", earliest_start_date: "" });
  const [savingConsent, setSavingConsent] = useState(false);
  const [resumeCandidate, setResumeCandidate] = useState(null);
  const [blockedDuplicates, setBlockedDuplicates] = useState([]);
  const [blockedOpen, setBlockedOpen] = useState(false);
  const [unblockingId, setUnblockingId] = useState(null);
  const [copyTarget, setCopyTarget] = useState(null);

  const resetFilters = () => setFilters({ search: "", status: "", source: "", consent: "", workAuth: "" });

  const filteredCandidates = candidates.filter((c) => {
    if (filters.search) {
      const q = filters.search.toLowerCase();
      if (!c.name?.toLowerCase().includes(q) && !c.email?.toLowerCase().includes(q)) return false;
    }
    if (filters.status && c.status !== filters.status) return false;
    if (filters.source && c.source !== filters.source) return false;
    if (filters.consent) {
      const cs = c.consent_status || (c.source === "apply_link" ? (c.consent ? "consented" : "not_required") : "none");
      if (filters.consent === "consented" && cs !== "consented") return false;
      if (filters.consent === "not_required" && cs !== "not_required") return false;
      if (filters.consent === "none" && cs !== "none") return false;
    }
    if (filters.workAuth && c.work_authorization !== filters.workAuth) return false;
    return true;
  });

  const loadCandidates = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/company/roles/${role.company_role_id}/candidates?sort_by=${sortBy}&sort_dir=${sortDir}`);
      setCandidates(data.candidates || []);
    } catch { toast.error("Could not load candidates"); }
    finally { setLoading(false); }
  }, [role.company_role_id, sortBy, sortDir]);

  const loadSetup = useCallback(async () => {
    try {
      const [setupRes, consentConfigRes, blockedRes] = await Promise.all([
        api.get(`/company/roles/${role.company_role_id}/interview-setup`),
        api.get(`/company/roles/${role.company_role_id}/consent-config`).catch(() => ({ data: { consent_enabled: false } })),
        api.get(`/company/roles/${role.company_role_id}/blocked-duplicates`).catch(() => ({ data: { blocks: [] } })),
      ]);
      const setup = setupRes.data;
      setSetupData(setup);
      setHasQuestions((setup.questions || []).some((q) => q.selected && q.text?.trim()));
      const { template_customized: _tc, ...consentData } = consentConfigRes.data;
      setRoleConsentConfig(consentData);
      setBlockedDuplicates(blockedRes.data.blocks || []);
    } catch { /* non-critical — toast shown only for candidates */ }
  }, [role.company_role_id]);

  const load = useCallback(async () => {
    await Promise.all([loadCandidates(), loadSetup()]);
  }, [loadCandidates, loadSetup]);

  useEffect(() => { loadCandidates(); }, [loadCandidates]);
  useEffect(() => { loadSetup(); }, [loadSetup]);

  const tabSwitchMountedRef = useRef(false);
  useEffect(() => {
    if (!tabSwitchMountedRef.current) { tabSwitchMountedRef.current = true; return; }
    if (tab === "candidates") loadCandidates();
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir((d) => d === "desc" ? "asc" : "desc");
    else { setSortBy(col); setSortDir("desc"); }
  };

  const handleUnblock = async (blockId) => {
    setUnblockingId(blockId);
    try {
      await api.post(`/company/roles/${role.company_role_id}/blocked-duplicates/${blockId}/unblock`);
      setBlockedDuplicates((prev) => prev.filter((b) => b.block_id !== blockId));
      toast.success("Candidate unblocked and added to pipeline");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not unblock candidate");
    } finally {
      setUnblockingId(null);
    }
  };

  const patchCandidate = async (id, updates) => {
    try {
      const { data } = await api.patch(`/company/roles/${role.company_role_id}/candidates/${id}`, updates);
      setCandidates((cs) => cs.map((c) => c.candidate_id === id ? { ...c, ...data } : c));
    } catch { toast.error("Update failed"); }
  };

  const handleSetStatus = (candidate, newStatus) => {
    if (newStatus === "hired") {
      setHireTarget(candidate);
    } else if (candidate.status === "hired" && newStatus === "completed") {
      api.patch(`/company/roles/${role.company_role_id}/candidates/${candidate.candidate_id}/unhire`)
        .then(({ data }) => setCandidates((cs) => cs.map((c) => c.candidate_id === candidate.candidate_id ? { ...c, ...data } : c)))
        .catch(() => toast.error("Could not undo hire"));
    } else {
      patchCandidate(candidate.candidate_id, { status: newStatus, shortlisted: newStatus === "shortlisted" });
    }
  };

  const confirmHire = async (hireData) => {
    try {
      const { data } = await api.patch(
        `/company/roles/${role.company_role_id}/candidates/${hireTarget.candidate_id}/hire`,
        hireData
      );
      setCandidates((cs) => cs.map((c) => c.candidate_id === hireTarget.candidate_id ? { ...c, ...data } : c));
      toast.success(`${hireTarget.name} marked as hired`);
      setHireTarget(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not record hire");
    }
  };

  const startEdit = (c) => {
    setEditId(c.candidate_id);
    setEditForm({ name: c.name || "", email: c.email || "", phone: c.phone || "" });
    setEditConsentForm({
      consent_given: !!c.consent,
      work_authorization: c.consent?.work_authorization || "",
      notice_period_days: c.consent?.notice_period_days ?? "",
      earliest_start_date: c.consent?.earliest_start_date || "",
    });
  };

  const saveEdit = async () => {
    if (!editForm.name.trim() || !editForm.email.trim()) { toast.error("Name and email are required"); return; }
    setEditSaving(true);
    try {
      const calls = [
        api.patch(`/company/roles/${role.company_role_id}/candidates/${editId}`, {
          name: editForm.name.trim(), email: editForm.email.trim(), phone: editForm.phone.trim() || null,
        }),
      ];
      if (roleConsentConfig?.consent_enabled) {
        calls.push(api.patch(`/company/roles/${role.company_role_id}/candidates/${editId}/consent`, {
          consent_given: editConsentForm.consent_given,
          work_authorization: editConsentForm.work_authorization || null,
          notice_period_days: editConsentForm.notice_period_days !== "" ? parseInt(editConsentForm.notice_period_days) : null,
          earliest_start_date: editConsentForm.earliest_start_date || null,
        }));
      }
      const results = await Promise.allSettled(calls);
      if (results[0].status === "rejected") throw results[0].reason;
      const updated = results[results.length - 1].status === "fulfilled"
        ? results[results.length - 1].value.data
        : results[0].value.data;
      setCandidates((cs) => cs.map((c) => c.candidate_id === editId ? { ...c, ...updated } : c));
      setEditId(null);
      toast.success("Candidate updated");
    } catch { toast.error("Could not save changes"); }
    finally { setEditSaving(false); }
  };

  const cancelEdit = () => setEditId(null);

  const confirmDeleteCandidate = async () => {
    if (!deleteCandidate) return;
    try {
      await api.delete(`/company/roles/${role.company_role_id}/candidates/${deleteCandidate.candidate_id}`);
      setCandidates((cs) => cs.filter((c) => c.candidate_id !== deleteCandidate.candidate_id));
      toast.success("Candidate removed");
    } catch { toast.error("Delete failed"); }
    finally { setDeleteCandidate(null); }
  };

  const shortlistTop5 = () => {
    const top = [...candidates]
      .filter((c) => c.match_score != null && c.status !== "rejected")
      .sort((a, b) => b.match_score - a.match_score)
      .slice(0, 5);
    top.forEach((c) => { if (!c.shortlisted) patchCandidate(c.candidate_id, { shortlisted: true }); });
    toast.success(`Top ${top.length} candidates shortlisted`);
  };

  const sendInvites = async () => {
    setSending(true);
    sendInvitesProgress.start();
    try {
      const { data } = await api.post(`/company/roles/${role.company_role_id}/interview-invites`);
      toast.success(`${data.sent} invite${data.sent !== 1 ? "s" : ""} sent${data.failed ? ` (${data.failed} failed)` : ""}`);
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = (detail && typeof detail === "object") ? detail.message : detail;
      toast.error(msg || "Could not send invites");
    } finally { setSending(false); sendInvitesProgress.finish(); setSendConfirm(false); }
  };

  const shortlistedCount = candidates.filter((c) => c.shortlisted && c.status !== "invited").length;
  const filteredShortlistedCount = filteredCandidates.filter((c) => c.shortlisted && c.status !== "invited").length;
  const isFiltered = !!(filters.search || filters.status || filters.source || filters.consent || filters.workAuth);
  const SortIcon = ({ col }) => sortBy === col
    ? (sortDir === "desc" ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />)
    : <ChevronDown className="w-3 h-3 opacity-30" />;

  return (
    <div className="p-6">
      {/* Actions bar */}
      <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            onClick={() => !isLocked && setShowAdd(true)}
            disabled={isLocked}
            title={isLocked ? "Activate your account to add candidates" : undefined}
            className="rounded-xl bg-[#0025D8] hover:bg-[#001CB3] disabled:opacity-50 disabled:cursor-not-allowed">
            <UserPlus className="w-4 h-4 mr-2" /> Add Candidate
          </Button>
          {candidates.some((c) => c.match_score != null) && (
            <button onClick={shortlistTop5}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg border border-[#F59E0B]/50 text-[#F59E0B] bg-[#F59E0B]/10 hover:bg-[#F59E0B] hover:text-white transition-all">
              <Star className="w-3.5 h-3.5" /> Top 5 by score
            </button>
          )}
        </div>
        <div className="relative group/invite">
          <button
            disabled={shortlistedCount === 0 || sending || !hasQuestions || isLocked}
            onClick={() => setSendConfirm(true)}
            title={isLocked ? "Activate your account to send invites" : undefined}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-bold border transition-all ${
              shortlistedCount > 0 && hasQuestions && !isLocked
                ? "bg-[#10B981]/10 text-[#10B981] border-[#10B981]/40 hover:bg-[#10B981] hover:text-white"
                : "opacity-40 cursor-not-allowed bg-secondary text-muted-foreground border-border"
            }`}>
            <Send className="w-4 h-4" />
            Send Interview Invites{shortlistedCount > 0 ? ` (${shortlistedCount})` : ""}
          </button>
          {!hasQuestions && (
            <div className="absolute bottom-full right-0 mb-2 w-56 bg-[#0F172A] text-white text-xs font-mono px-3 py-2 pointer-events-none opacity-0 group-hover/invite:opacity-100 transition-opacity z-20 rounded-lg border border-[#333]">
              Set up interview questions first in the Interview Setup tab.
            </div>
          )}
          {isFiltered && shortlistedCount > 0 && filteredShortlistedCount !== shortlistedCount && (
            <div className="font-mono text-[9px] text-muted-foreground text-right mt-1">
              {filteredShortlistedCount} of {shortlistedCount} shortlisted visible
            </div>
          )}
        </div>
      </div>

      {/* Pipeline stats */}
      {!loading && candidates.length > 0 && <PipelineStats candidates={candidates} />}

      {/* Apply link */}
      <ApplyLinkBox roleId={role.company_role_id} roleStatus={role.status} />

      <CandidateFilters
        filters={filters}
        onChange={setFilters}
        total={candidates.length}
        filtered={filteredCandidates.length}
        onReset={resetFilters}
      />

      {loading ? (
        <div className="p-12 font-mono text-sm text-muted-foreground text-center">Loading candidates…</div>
      ) : candidates.length === 0 ? (
        <div className="border border-dashed border-border p-14 text-center">
          <Users className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
          <p className="font-semibold mb-1">No candidates yet.</p>
          <p className="text-sm text-muted-foreground">Add your first candidate by uploading a resume or entering details manually.</p>
        </div>
      ) : filteredCandidates.length === 0 ? (
        <div className="border border-dashed border-border p-12 text-center">
          <Search className="w-6 h-6 text-muted-foreground mx-auto mb-3" />
          <p className="font-semibold mb-1">No candidates match these filters.</p>
          <button onClick={resetFilters} className="text-sm text-[#0025D8] hover:underline mt-1">Clear all filters</button>
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block rounded-xl border border-border overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-secondary/40 border-b border-border">
                  {[
                    { col: "name", label: "Candidate" },
                    { col: null, label: "Contact" },
                    { col: null, label: "Consent" },
                    { col: "match_score", label: "Match" },
                    { col: "interview_score", label: "Interview" },
                    { col: "status", label: "Status" },
                    { col: "added_at", label: "Added" },
                    { col: null, label: "" },
                  ].map(({ col, label }, i) => (
                    <th key={i} className={`text-left p-3 font-mono text-[10px] tracking-[0.2em] text-muted-foreground border-r border-border last:border-r-0 ${col ? "cursor-pointer hover:text-foreground select-none" : ""}`}
                      onClick={() => col && toggleSort(col)}>
                      <div className="flex items-center gap-1">
                        {label}{col && <SortIcon col={col} />}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredCandidates.map((c) => (
                  <React.Fragment key={c.candidate_id}>
                  <tr className={`border-b border-border transition-colors ${editId === c.candidate_id ? "bg-[#F59E0B]/5" : c.shortlisted ? "bg-[#0025D8]/3 hover:bg-[#0025D8]/5" : "hover:bg-secondary/20"}`}>
                    {/* Name */}
                    <td className="p-3 border-r border-border">
                      <div className="flex items-center gap-2">
                        <button onClick={() => patchCandidate(c.candidate_id, { shortlisted: !c.shortlisted })}
                          className={`flex-shrink-0 transition-colors ${c.shortlisted ? "text-[#F59E0B]" : "text-muted-foreground hover:text-[#F59E0B]"}`}
                          title={c.shortlisted ? "Remove from shortlist" : "Shortlist"}>
                          {c.shortlisted ? <Star className="w-3.5 h-3.5 fill-current" /> : <Star className="w-3.5 h-3.5" />}
                        </button>
                        {editId === c.candidate_id ? (
                          <input
                            value={editForm.name}
                            onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                            className="rounded-lg border border-[#F59E0B]/60 px-2 py-1 text-sm w-36 focus:outline-none focus:border-[#F59E0B]"
                            placeholder="Full name"
                            autoFocus
                          />
                        ) : (
                          <div>
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="font-semibold text-sm">{c.name}</span>
                              {c.source === "apply_link" && (
                                <span className="font-mono text-[9px] px-1.5 py-0.5 bg-[#7C3AED]/10 text-[#7C3AED] rounded-lg border border-[#7C3AED]/30">APPLIED</span>
                              )}
                            </div>
                            {c.location && <div className="text-xs text-muted-foreground">{c.location}</div>}
                            {c.hire_record && (
                              <div className="mt-0.5 space-y-0.5">
                                {c.hire_record.offer_date && (
                                  <div className="font-mono text-[9px] text-[#B45309]">Offer: {c.hire_record.offer_date}</div>
                                )}
                                {c.hire_record.salary_offered && (
                                  <div className="font-mono text-[9px] text-[#B45309]">{c.hire_record.salary_offered}</div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    {/* Email / Phone */}
                    <td className="p-3 border-r border-border">
                      {editId === c.candidate_id ? (
                        <div className="space-y-1">
                          <input
                            value={editForm.email}
                            onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
                            className="rounded-lg border border-[#F59E0B]/60 px-2 py-1 text-xs w-40 focus:outline-none focus:border-[#F59E0B]"
                            placeholder="email@example.com"
                            type="email"
                          />
                          <input
                            value={editForm.phone}
                            onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))}
                            className="rounded-lg border border-[#F59E0B]/60 px-2 py-1 text-xs w-40 focus:outline-none focus:border-[#F59E0B]"
                            placeholder="Phone (optional)"
                          />
                        </div>
                      ) : (
                        <>
                          <div className="text-xs">{c.email}</div>
                          {c.phone && <div className="text-xs text-muted-foreground">{c.phone}</div>}
                          {c.linkedin_url && (
                            <a href={c.linkedin_url} target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 font-mono text-[9px] text-[#0A66C2] hover:underline mt-0.5">
                              <Linkedin className="w-2.5 h-2.5" />LinkedIn
                            </a>
                          )}
                          {c.work_authorization && (
                            <div className="font-mono text-[9px] text-muted-foreground mt-0.5 flex items-center gap-1">
                              <ShieldCheck className="w-2.5 h-2.5 text-[#10B981]" />{c.work_authorization}
                            </div>
                          )}
                        </>
                      )}
                    </td>
                    {/* Consent */}
                    <td className="p-3 border-r border-border">
                      {c.consent ? (
                        <div className="space-y-1">
                          {c.consent.source === "recorded_by_company" ? (
                            <span className="font-mono text-[9px] px-1.5 py-0.5 bg-[#F59E0B]/10 text-[#B45309] rounded-lg border border-[#F59E0B]/30 flex items-center gap-1 w-fit">
                              <ShieldCheck className="w-2.5 h-2.5" />RECORDED
                            </span>
                          ) : (
                            <span className="font-mono text-[9px] px-1.5 py-0.5 bg-[#10B981]/10 text-[#10B981] rounded-lg border border-[#10B981]/30 flex items-center gap-1 w-fit">
                              <ShieldCheck className="w-2.5 h-2.5" />CONSENTED
                            </span>
                          )}
                          <div className="font-mono text-[9px] text-muted-foreground">{new Date(c.consent.given_at).toLocaleDateString()}</div>
                          {c.consent.work_authorization && (
                            <div className="font-mono text-[9px] text-muted-foreground truncate max-w-[130px]" title={c.consent.work_authorization}>{c.consent.work_authorization}</div>
                          )}
                          {c.consent.notice_period_days != null && (
                            <div className="font-mono text-[9px] text-muted-foreground">{c.consent.notice_period_days}d notice</div>
                          )}
                          {c.consent.earliest_start_date && (
                            <div className="font-mono text-[9px] text-muted-foreground">Start: {c.consent.earliest_start_date}</div>
                          )}
                        </div>
                      ) : c.source === "apply_link" ? (
                        <span className="font-mono text-[9px] text-muted-foreground">Not required</span>
                      ) : (
                        <span className="font-mono text-[9px] text-muted-foreground">—</span>
                      )}
                    </td>
                    {/* Match */}
                    <td className="p-3 border-r border-border">
                      {c.match_score != null ? (
                        <span className={`font-mono font-bold text-sm ${scoreColor(c.match_score)}`}>{Math.round(c.match_score)}%</span>
                      ) : (
                        <span className="font-mono text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    {/* Interview score */}
                    <td className="p-3 border-r border-border">
                      <div className="flex items-center gap-1.5">
                        {c.interview_score != null ? (
                          <span className={`font-mono font-bold text-sm ${scoreColor(c.interview_score)}`}>{Math.round(c.interview_score)}%</span>
                        ) : c.status === "invited" ? (
                          <span className="font-mono text-[10px] px-1.5 py-0.5 border bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/30">Awaiting</span>
                        ) : (
                          <span className="font-mono text-xs text-muted-foreground">—</span>
                        )}
                        {c.cheat_events_count > 0 && (
                          c.cheat_events_count <= 2 ? (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-mono font-bold bg-yellow-500/10 text-yellow-600 border border-yellow-500/30" title={`${c.cheat_events_count} integrity event${c.cheat_events_count !== 1 ? "s" : ""} — click to review`}>
                              ⚠ {c.cheat_events_count}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-mono font-bold bg-[#E11D48]/10 text-[#E11D48] rounded-lg border border-[#E11D48]/30" title={`${c.cheat_events_count} integrity events — review required`}>
                              ✗ {c.cheat_events_count}
                            </span>
                          )
                        )}
                      </div>
                    </td>
                    {/* Status — progress bar */}
                    <td className="p-3 border-r border-border">
                      <StatusBar
                        status={c.status}
                        onSetStatus={(newStatus) => handleSetStatus(c, newStatus)}
                      />
                    </td>
                    {/* Date */}
                    <td className="p-3 border-r border-border">
                      <span className="font-mono text-[10px] text-muted-foreground">{new Date(c.added_at).toLocaleDateString()}</span>
                    </td>
                    {/* Actions */}
                    <td className="p-3">
                      {editId === c.candidate_id ? (
                        <div className="flex items-center gap-1.5">
                          <button onClick={saveEdit} disabled={editSaving}
                            className="flex items-center gap-1 font-mono text-[10px] px-2 py-1 bg-[#0025D8] text-white hover:bg-[#001CB3] transition-colors disabled:opacity-50">
                            <Check className="w-3 h-3" />{editSaving ? "…" : "Save"}
                          </button>
                          <button onClick={cancelEdit} className="font-mono text-[10px] px-2 py-1 rounded-xl border border-border text-muted-foreground hover:text-foreground transition-colors">
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          {c.status === "completed" && (
                            <button onClick={() => setViewResult(c)}
                              className="font-mono text-[10px] px-2 py-1 rounded-lg border border-[#0025D8]/30 text-[#0025D8] hover:bg-[#0025D8] hover:text-white transition-all whitespace-nowrap">
                              View Result
                            </button>
                          )}
                          {c.status === "invited" && (
                            <button onClick={() => setResendTarget(c)} className="text-muted-foreground hover:text-[#0025D8] transition-colors" title="Resend invite">
                              <RotateCcw className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {c.consent && (
                            <button onClick={() => downloadConsent(c, role)} className="text-muted-foreground hover:text-[#10B981] transition-colors" title="Download consent record">
                              <Download className="w-3.5 h-3.5" />
                            </button>
                          )}
                          <button
                            onClick={() => c.has_resume ? setResumeCandidate(c) : null}
                            disabled={!c.has_resume}
                            className={`transition-colors ${c.has_resume ? "text-muted-foreground hover:text-[#0025D8]" : "text-muted-foreground/30 cursor-not-allowed"}`}
                            title={c.has_resume ? "View resume" : "No resume on file"}
                          >
                            <FileText className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => setCopyTarget(c)} className="text-muted-foreground hover:text-[#10B981] transition-colors" title="Copy to another role">
                            <Copy className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => startEdit(c)} className="text-muted-foreground hover:text-foreground transition-colors" title="Edit contact">
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => setDeleteCandidate(c)} className="text-muted-foreground hover:text-[#E11D48] transition-colors" title="Delete">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                  {/* Consent expansion row — shown when editing and role has consent enabled */}
                  {editId === c.candidate_id && roleConsentConfig?.consent_enabled && (
                    <tr className="bg-[#0025D8]/3 border-b border-[#0025D8]/20">
                      <td colSpan={8} className="px-6 py-4">
                        <div className="font-mono text-[9px] tracking-[0.2em] text-[#0025D8] mb-3 flex items-center gap-1.5">
                          <ShieldCheck className="w-3 h-3" />CONSENT — RECORDED BY COMPANY
                        </div>
                        <div className="flex items-center gap-3 flex-wrap">
                          <label className="flex items-center gap-2 cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={editConsentForm.consent_given}
                              onChange={(e) => setEditConsentForm((f) => ({ ...f, consent_given: e.target.checked }))}
                              className="w-3.5 h-3.5 accent-[#0025D8]"
                            />
                            <span className="text-sm font-medium">Consent given</span>
                          </label>
                          {editConsentForm.consent_given && (
                            <>
                              {roleConsentConfig.require_work_authorization && (
                                <select
                                  value={editConsentForm.work_authorization}
                                  onChange={(e) => setEditConsentForm((f) => ({ ...f, work_authorization: e.target.value }))}
                                  className="rounded-xl border border-border px-2 py-1.5 text-xs bg-white focus:outline-none focus:border-[#0025D8] min-w-[180px]"
                                >
                                  <option value="">Work Auth…</option>
                                  {WORK_AUTH_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                                </select>
                              )}
                              {roleConsentConfig.require_notice_period && (
                                <input
                                  type="number" min="0" max="365"
                                  value={editConsentForm.notice_period_days}
                                  onChange={(e) => setEditConsentForm((f) => ({ ...f, notice_period_days: e.target.value }))}
                                  placeholder="Notice days"
                                  className="rounded-xl border border-border px-2 py-1.5 text-xs w-32 focus:outline-none focus:border-[#0025D8]"
                                />
                              )}
                              {roleConsentConfig.require_earliest_start && (
                                <input
                                  type="date"
                                  value={editConsentForm.earliest_start_date}
                                  min={new Date().toISOString().slice(0, 10)}
                                  onChange={(e) => setEditConsentForm((f) => ({ ...f, earliest_start_date: e.target.value }))}
                                  className="rounded-xl border border-border px-2 py-1.5 text-xs focus:outline-none focus:border-[#0025D8]"
                                />
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile card list */}
          <div className="md:hidden space-y-3">
            {filteredCandidates.map((c) => (
              <div key={c.candidate_id} className={`rounded-xl border border-border p-4 ${c.shortlisted ? "border-[#0025D8]/30 bg-[#0025D8]/5" : "bg-white"}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <button onClick={() => patchCandidate(c.candidate_id, { shortlisted: !c.shortlisted })}
                        className={c.shortlisted ? "text-[#F59E0B]" : "text-muted-foreground"}>
                        <Star className={`w-3.5 h-3.5 ${c.shortlisted ? "fill-current" : ""}`} />
                      </button>
                      <span className="font-semibold">{c.name}</span>
                      {c.source === "apply_link" && (
                        <span className="font-mono text-[9px] px-1.5 py-0.5 bg-[#7C3AED]/10 text-[#7C3AED] rounded-lg border border-[#7C3AED]/30">APPLIED</span>
                      )}
                      {c.consent && (
                        c.consent.source === "recorded_by_company" ? (
                          <span className="font-mono text-[9px] px-1.5 py-0.5 bg-[#F59E0B]/10 text-[#B45309] rounded-lg border border-[#F59E0B]/30 flex items-center gap-0.5">
                            <ShieldCheck className="w-2.5 h-2.5" />RECORDED
                          </span>
                        ) : (
                          <span className="font-mono text-[9px] px-1.5 py-0.5 bg-[#10B981]/10 text-[#10B981] rounded-lg border border-[#10B981]/30 flex items-center gap-0.5">
                            <ShieldCheck className="w-2.5 h-2.5" />CONSENTED
                          </span>
                        )
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">{c.email}</div>
                    {c.linkedin_url && (
                      <a href={c.linkedin_url} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 font-mono text-[9px] text-[#0A66C2] hover:underline mt-0.5">
                        <Linkedin className="w-2.5 h-2.5" />LinkedIn
                      </a>
                    )}
                    {c.consent?.work_authorization && (
                      <div className="font-mono text-[9px] text-muted-foreground mt-0.5">{c.consent.work_authorization}</div>
                    )}
                    {c.consent?.notice_period_days != null && (
                      <div className="font-mono text-[9px] text-muted-foreground">{c.consent.notice_period_days}d notice</div>
                    )}
                    {c.consent?.earliest_start_date && (
                      <div className="font-mono text-[9px] text-muted-foreground">Start: {c.consent.earliest_start_date}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {c.match_score != null && (
                      <div className="text-right">
                        <div className="font-mono text-[9px] text-muted-foreground">MATCH</div>
                        <span className={`font-mono font-bold text-base ${scoreColor(c.match_score)}`}>{Math.round(c.match_score)}%</span>
                      </div>
                    )}
                    {c.interview_score != null && (
                      <div className="text-right">
                        <div className="font-mono text-[9px] text-muted-foreground">INTERVIEW</div>
                        <div className="flex items-center justify-end gap-1.5">
                          <span className={`font-mono font-bold text-base ${scoreColor(c.interview_score)}`}>{Math.round(c.interview_score)}%</span>
                          {c.cheat_events_count > 0 && (
                            c.cheat_events_count <= 2 ? (
                              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-mono font-bold bg-yellow-500/10 text-yellow-600 border border-yellow-500/30" title={`${c.cheat_events_count} integrity event${c.cheat_events_count !== 1 ? "s" : ""}`}>
                                ⚠ {c.cheat_events_count}
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-mono font-bold bg-[#E11D48]/10 text-[#E11D48] rounded-lg border border-[#E11D48]/30" title={`${c.cheat_events_count} integrity events — review required`}>
                                ✗ {c.cheat_events_count}
                              </span>
                            )
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <StatusBar
                    status={c.status}
                    onSetStatus={(newStatus) => handleSetStatus(c, newStatus)}
                  />
                  <div className="flex items-center gap-2">
                    {c.status === "invited" && (
                      <button onClick={() => setResendTarget(c)} className="text-muted-foreground hover:text-[#0025D8] transition-colors" title="Resend invite">
                        <RotateCcw className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {c.consent && (
                      <button onClick={() => downloadConsent(c, role)} className="text-muted-foreground hover:text-[#10B981] transition-colors" title="Download consent record">
                        <Download className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button onClick={() => setCopyTarget(c)} className="text-muted-foreground hover:text-[#10B981] transition-colors" title="Copy to another role">
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => startEdit(c)} className="text-muted-foreground hover:text-foreground transition-colors" title="Edit contact">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => setDeleteCandidate(c)} className="text-muted-foreground hover:text-[#E11D48]">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Blocked Duplicates */}
      {blockedDuplicates.length > 0 && (
        <div className="mt-8 rounded-lg border border-[#E11D48]/20 bg-[#E11D48]/5">
          <button
            onClick={() => setBlockedOpen((o) => !o)}
            className="w-full flex items-center justify-between px-4 py-3 text-left"
          >
            <div className="flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 text-[#E11D48]" />
              <span className="font-mono text-[10px] tracking-[0.15em] text-[#E11D48] font-bold">
                BLOCKED DUPLICATES ({blockedDuplicates.length})
              </span>
            </div>
            {blockedOpen ? <ChevronUp className="w-4 h-4 text-[#E11D48]" /> : <ChevronDown className="w-4 h-4 text-[#E11D48]" />}
          </button>
          {blockedOpen && (
            <div className="border-t border-[#E11D48]/20">
              <div className="px-4 py-2 bg-[#E11D48]/5 border-b border-[#E11D48]/10">
                <p className="font-mono text-[10px] text-muted-foreground">
                  These applications were silently blocked at apply time due to suspected duplicate identity. Review and unblock if this is a false positive.
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-[#E11D48]/10">
                      <th className="text-left px-4 py-2 font-mono text-[10px] tracking-[0.1em] text-muted-foreground font-normal">NAME ATTEMPTED</th>
                      <th className="text-left px-4 py-2 font-mono text-[10px] tracking-[0.1em] text-muted-foreground font-normal">EMAIL ATTEMPTED</th>
                      <th className="text-left px-4 py-2 font-mono text-[10px] tracking-[0.1em] text-muted-foreground font-normal">MATCH TYPE</th>
                      <th className="text-left px-4 py-2 font-mono text-[10px] tracking-[0.1em] text-muted-foreground font-normal">SIMILARITY</th>
                      <th className="text-left px-4 py-2 font-mono text-[10px] tracking-[0.1em] text-muted-foreground font-normal">MATCHED CANDIDATE</th>
                      <th className="text-left px-4 py-2 font-mono text-[10px] tracking-[0.1em] text-muted-foreground font-normal">BLOCKED</th>
                      <th className="px-4 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {blockedDuplicates.map((b) => (
                      <tr key={b.block_id} className="border-b border-[#E11D48]/10 last:border-0 hover:bg-[#E11D48]/5 transition-colors">
                        <td className="px-4 py-3 font-medium">{b.attempted_name}</td>
                        <td className="px-4 py-3 font-mono text-[11px] text-muted-foreground">{b.attempted_email}</td>
                        <td className="px-4 py-3">
                          <span className={`font-mono text-[9px] px-2 py-0.5 border tracking-[0.1em] font-bold ${
                            b.match_type === "linkedin_url"
                              ? "bg-[#0025D8]/10 text-[#0025D8] border-[#0025D8]/30"
                              : b.match_type === "name_phone"
                              ? "bg-[#F59E0B]/10 text-[#B45309] border-[#F59E0B]/40"
                              : "bg-[#E11D48]/10 text-[#E11D48] border-[#E11D48]/30"
                          }`}>
                            {b.match_type === "linkedin_url" ? "LINKEDIN URL" : b.match_type === "name_phone" ? "NAME + PHONE" : "RESUME SIMILARITY"}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-mono text-[11px]">
                          {b.match_type === "resume_similarity" ? `${Math.round((b.similarity_score || 0) * 100)}%` : "—"}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground text-[11px]">{b.matched_candidate_name || "—"}</td>
                        <td className="px-4 py-3 font-mono text-[11px] text-muted-foreground">
                          {b.blocked_at ? new Date(b.blocked_at).toLocaleDateString() : "—"}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => handleUnblock(b.block_id)}
                            disabled={unblockingId === b.block_id}
                            className="font-mono text-[9px] tracking-[0.1em] px-3 py-1 rounded-xl border border-border text-muted-foreground hover:text-foreground hover:border-foreground transition-colors disabled:opacity-40"
                          >
                            {unblockingId === b.block_id ? "UNBLOCKING…" : "UNBLOCK"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      {showAdd && <AddCandidateModal role={role} consentConfig={roleConsentConfig} onClose={() => setShowAdd(false)} onAdded={(c) => { setCandidates((cs) => [c, ...cs]); }} />}
      {deleteCandidate && (
        <ConfirmDialog
          danger
          message={
            deleteCandidate.status === "completed"
              ? `Permanently delete ${deleteCandidate.name}? This will remove their candidate record, interview transcript, evaluation, and video recording from storage. This cannot be undone.`
              : `Permanently delete ${deleteCandidate.name}? This will remove their candidate record and any associated data. This cannot be undone.`
          }
          confirmLabel="Delete permanently"
          onConfirm={confirmDeleteCandidate}
          onCancel={() => setDeleteCandidate(null)}
        />
      )}
      {sendConfirm && (
        <SendInvitePreviewModal
          role={role}
          candidates={candidates}
          setupData={setupData}
          sending={sending}
          sendProgress={sendInvitesProgress}
          onSend={sendInvites}
          onClose={() => setSendConfirm(false)}
        />
      )}

      {viewResult && <InviteResultModal roleId={role.company_role_id} candidate={viewResult} onClose={() => setViewResult(null)} />}
      {hireTarget && <HireModal candidate={hireTarget} onConfirm={confirmHire} onClose={() => setHireTarget(null)} />}
      {resumeCandidate && <ResumePanel candidate={resumeCandidate} roleTitle={role.title} onClose={() => setResumeCandidate(null)} />}
      {copyTarget && (
        <CopyToRoleModal
          candidate={copyTarget}
          sourceRoleId={role.company_role_id}
          onClose={() => setCopyTarget(null)}
          onCopied={() => setCopyTarget(null)}
        />
      )}
      {resendTarget && (
        <ResendInviteModal
          roleId={role.company_role_id}
          candidate={resendTarget}
          onClose={() => setResendTarget(null)}
          onResent={(updatedEmail) => {
            if (updatedEmail !== resendTarget.email) {
              setCandidates((cs) => cs.map((c) => c.candidate_id === resendTarget.candidate_id ? { ...c, email: updatedEmail } : c));
            }
            setResendTarget(null);
          }}
        />
      )}
    </div>
  );
}

// ── Interview Setup Tab ───────────────────────────────────────────────────────

function QuestionCard({ q, index, total, onChange, onDelete, onMoveUp, onMoveDown, role, autoFocus, onFocused, showBank }) {
  const [genLoading, setGenLoading] = useState(false);
  const [answerOpen, setAnswerOpen] = useState(true);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (autoFocus && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      onFocused?.();
    }
  }, [autoFocus, onFocused]);

  const generateKeyPoints = async () => {
    if (!q.text.trim()) { toast.error("Enter a question first"); return; }
    setGenLoading(true);
    try {
      const { data } = await api.post(`/company/roles/${role.company_role_id}/interview-setup/generate-key-points`, { question: q.text, bank: q.bank || "jd" });
      onChange(q.id, { key_points: data.key_points || [] });
    } catch { toast.error("Could not generate key points"); }
    finally { setGenLoading(false); }
  };

  const keyPoints = q.key_points?.length ? q.key_points : (q.answer ? [q.answer] : []);

  const updatePoint = (i, val) => {
    const pts = [...keyPoints];
    pts[i] = val;
    onChange(q.id, { key_points: pts });
  };
  const removePoint = (i) => onChange(q.id, { key_points: keyPoints.filter((_, idx) => idx !== i) });
  const addPoint = () => onChange(q.id, { key_points: [...keyPoints, ""] });

  // Read-only collapsible answer section (admin bank questions)
  const adminKeyPoints = q.key_points?.length ? q.key_points : [];
  const adminSampleAnswer = q.sample_answer || "";
  const hasAdminAnswer = adminKeyPoints.length > 0 || adminSampleAnswer;

  return (
    <div className={`border bg-white transition-all ${q.selected ? "border-border" : "border-border opacity-50"}`}>
      <div className="flex items-start gap-3 p-4">
        {/* Order controls */}
        <div className="flex flex-col items-center gap-0.5 flex-shrink-0 pt-0.5">
          <button onClick={onMoveUp} disabled={index === 0}
            className="text-muted-foreground hover:text-foreground disabled:opacity-20 transition-colors">
            <ChevronUp className="w-3.5 h-3.5" />
          </button>
          <span className="font-mono text-[10px] text-muted-foreground w-5 text-center">{index + 1}</span>
          <button onClick={onMoveDown} disabled={index === total - 1}
            className="text-muted-foreground hover:text-foreground disabled:opacity-20 transition-colors">
            <ChevronDown className="w-3.5 h-3.5" />
          </button>
        </div>

        <div className="flex-1 min-w-0 space-y-3">
          {/* Select + source badge */}
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => onChange(q.id, { selected: !q.selected })}
              className={`flex items-center gap-1.5 font-mono text-[10px] px-2 py-0.5 border transition-all ${q.selected ? "bg-[#0025D8] text-white border-[#0025D8]" : "text-muted-foreground border-border hover:border-[#0025D8]"}`}>
              {q.selected ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
              {q.selected ? "Selected" : "Include"}
            </button>
            <span className={`font-mono text-[9px] px-1.5 py-0.5 border tracking-[0.1em] ${
              q.source === "ai_generated" ? "bg-[#7C3AED]/10 text-[#7C3AED] border-[#7C3AED]/30"
              : q.source === "admin_bank" ? "bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/30"
              : "bg-secondary text-muted-foreground border-border"
            }`}>
              {q.source === "ai_generated" ? "AI" : q.source === "admin_bank" ? "Suggested" : "Manual"}
            </span>
            {showBank && (
              <span className={`font-mono text-[9px] px-1.5 py-0.5 border tracking-[0.1em] font-bold ${
                (q.bank || "jd") === "jd"
                  ? "bg-[#0025D8]/8 text-[#0025D8] border-[#0025D8]/30"
                  : "bg-[#7C3AED]/8 text-[#7C3AED] border-[#7C3AED]/30"
              }`}>
                {(q.bank || "jd") === "jd" ? "JD" : "DOC"}
              </span>
            )}
            {q.difficulty && (
              <span className={`font-mono text-[9px] px-1.5 py-0.5 border tracking-[0.1em] ${
                q.difficulty === "Easy"   ? "bg-[#10B981]/10 text-[#10B981] border-[#10B981]/30"
                : q.difficulty === "Hard" ? "bg-[#E11D48]/10 text-[#E11D48] border-[#E11D48]/30"
                : "bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/30"
              }`}>
                {q.difficulty}
              </span>
            )}
            {q.question_type && (
              <span className="font-mono text-[9px] px-1.5 py-0.5 border tracking-[0.1em] bg-secondary text-muted-foreground border-border">
                {q.question_type}
              </span>
            )}
            {q.competency && (
              <span className="font-mono text-[9px] px-1.5 py-0.5 border tracking-[0.1em] bg-[#0025D8]/5 text-[#0025D8] border-[#0025D8]/20">
                {q.competency}
              </span>
            )}
            {q.tier && (
              <span className={`font-mono text-[9px] px-1.5 py-0.5 border tracking-[0.1em] font-bold ${
                q.tier === "Critical"  ? "bg-[#E11D48]/10 text-[#E11D48] border-[#E11D48]/30"
                : q.tier === "Important" ? "bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/30"
                : "bg-secondary text-muted-foreground border-border"
              }`}>
                {q.tier}
              </span>
            )}
            {q.skill_tag && (
              <span className="font-mono text-[9px] px-1.5 py-0.5 border tracking-[0.1em] bg-[#7C3AED]/5 text-[#7C3AED] border-[#7C3AED]/20 max-w-[180px] truncate" title={q.skill_tag}>
                {q.skill_tag}
              </span>
            )}
          </div>

          {/* Question text */}
          <div>
            <div className="font-mono text-[10px] text-muted-foreground tracking-[0.15em] mb-1">QUESTION</div>
            <textarea
              ref={textareaRef}
              value={q.text}
              onChange={(e) => onChange(q.id, { text: e.target.value })}
              rows={2}
              className="w-full rounded-xl border border-border px-3 py-2 text-sm rounded-xl focus:outline-none focus:ring-1 focus:ring-[#0025D8] resize-none"
              placeholder="Enter question…"
            />
          </div>

          {/* Key points / answer */}
          {q.source === "admin_bank" ? (
            hasAdminAnswer && (
              <div>
                <button
                  onClick={() => setAnswerOpen((o) => !o)}
                  className="flex items-center gap-1 font-mono text-[10px] text-muted-foreground hover:text-[#0025D8] transition-colors group">
                  {answerOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  <span className="uppercase tracking-wider">Key Points</span>
                  {adminKeyPoints.length > 0 && (
                    <span className="ml-1 px-1 py-0 bg-secondary rounded-xl border border-border text-[9px] font-semibold">{adminKeyPoints.length}</span>
                  )}
                </button>
                {answerOpen && (
                  <ul className="mt-1.5 ml-0.5 space-y-1 border-l-2 border-[#0025D8]/20 pl-3">
                    {adminKeyPoints.length > 0
                      ? adminKeyPoints.map((pt, i) => (
                          <li key={i} className="flex items-start gap-1.5">
                            <span className="text-[#0025D8] font-bold text-[10px] flex-shrink-0 mt-0.5">·</span>
                            <span className="text-[11px] text-muted-foreground leading-snug">{pt}</span>
                          </li>
                        ))
                      : <li className="text-[11px] text-muted-foreground leading-relaxed italic">{adminSampleAnswer}</li>
                    }
                  </ul>
                )}
              </div>
            )
          ) : (
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="font-mono text-[10px] text-muted-foreground tracking-[0.15em]">KEY ANSWER POINTS</div>
                <button onClick={generateKeyPoints} disabled={genLoading}
                  className="flex items-center gap-1 font-mono text-[9px] px-2 py-0.5 rounded-lg border border-[#7C3AED]/40 text-[#7C3AED] bg-[#7C3AED]/10 hover:bg-[#7C3AED] hover:text-white transition-all disabled:opacity-50">
                  <Lightbulb className={`w-3 h-3 ${genLoading ? "animate-pulse" : ""}`} />
                  {genLoading ? "Generating…" : "Generate with AI"}
                </button>
              </div>
              <div className="space-y-1.5">
                {keyPoints.map((pt, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <span className="text-[#7C3AED] font-bold text-xs mt-2 flex-shrink-0">•</span>
                    <input
                      value={pt}
                      onChange={(e) => updatePoint(i, e.target.value)}
                      className="flex-1 rounded-xl border border-border px-2 py-1.5 text-xs rounded-xl focus:outline-none focus:ring-1 focus:ring-[#7C3AED] text-muted-foreground"
                      placeholder="Key point…"
                    />
                    <button onClick={() => removePoint(i)} className="text-muted-foreground hover:text-[#E11D48] transition-colors mt-1.5 flex-shrink-0">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                <button onClick={addPoint}
                  className="flex items-center gap-1 font-mono text-[9px] text-muted-foreground hover:text-[#7C3AED] transition-colors mt-0.5">
                  <Plus className="w-3 h-3" /> Add point
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Delete */}
        <button onClick={() => onDelete(q.id)}
          className="text-muted-foreground hover:text-[#E11D48] transition-colors flex-shrink-0 mt-1">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

const BANK_ROUND_LABELS = { hr: "HR", technical: "Technical", behavioral: "Behavioral" };

function BankImportModal({ role, bankMatch, onImport, onClose }) {
  const [round, setRound] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const availableRounds = Object.entries(bankMatch.rounds).filter(([, count]) => count > 0);

  const loadQuestions = async (r, off) => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/company/roles/${role.company_role_id}/interview-setup/bank-questions`,
        { params: { admin_role_id: bankMatch.admin_role_id, round: r, offset: off } }
      );
      setQuestions((prev) => off === 0 ? data.questions : [...prev, ...data.questions]);
      setTotal(data.total);
      setOffset(off + data.questions.length);
    } catch {
      toast.error("Could not load questions");
    } finally {
      setLoading(false);
    }
  };

  const pickRound = (r) => {
    setRound(r);
    setQuestions([]);
    setSelected(new Set());
    setOffset(0);
    loadQuestions(r, 0);
  };

  const toggleQ = (qId) => setSelected((prev) => {
    const n = new Set(prev);
    n.has(qId) ? n.delete(qId) : n.add(qId);
    return n;
  });

  const doImport = () => {
    onImport(questions.filter((q) => selected.has(q.q_id)));
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl border border-border w-full max-w-2xl max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-border flex-shrink-0">
          <div>
            <div className="font-mono text-[10px] text-muted-foreground tracking-[0.2em] mb-0.5">IMPORT FROM BANK</div>
            <h2 className="font-bold text-base">{bankMatch.admin_role_name}</h2>
          </div>
          <button onClick={onClose} className="p-1 hover:text-foreground text-muted-foreground"><X className="w-5 h-5" /></button>
        </div>

        {!round && (
          <div className="p-6">
            <p className="text-sm text-muted-foreground mb-4">Select which interview round to import questions from:</p>
            <div className="grid grid-cols-3 gap-3">
              {availableRounds.map(([r, count]) => (
                <button key={r} onClick={() => pickRound(r)}
                  className="rounded-xl border border-border p-4 text-center hover:border-[#7C3AED] hover:bg-[#7C3AED]/5 transition-colors group">
                  <div className="font-bold text-sm group-hover:text-[#7C3AED]">{BANK_ROUND_LABELS[r]}</div>
                  <div className="font-mono text-[10px] text-muted-foreground mt-1">{count} question{count !== 1 ? "s" : ""}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {round && (
          <>
            <div className="flex items-center gap-2 px-6 py-2.5 border-b border-border bg-secondary/30 flex-shrink-0">
              <button onClick={() => { setRound(null); setQuestions([]); setSelected(new Set()); }}
                className="font-mono text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors">
                <ChevronUp className="w-3 h-3 -rotate-90" /> Back
              </button>
              <span className="font-mono text-[10px] text-muted-foreground">
                · {BANK_ROUND_LABELS[round]} · {questions.length} of {total} loaded · {selected.size} selected
              </span>
            </div>
            <div className="overflow-y-auto flex-1 divide-y divide-border">
              {questions.map((q) => {
                const sel = selected.has(q.q_id);
                return (
                  <label key={q.q_id} className={`flex items-start gap-3 px-6 py-3 cursor-pointer hover:bg-secondary/30 transition-colors ${sel ? "bg-[#7C3AED]/5" : ""}`}>
                    <input type="checkbox" checked={sel} onChange={() => toggleQ(q.q_id)} className="mt-0.5 accent-[#7C3AED] flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm leading-relaxed">{q.text}</p>
                      {q.category && (
                        <span className="mt-1.5 inline-block font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-[#1D4ED8]/10 text-[#1D4ED8] rounded-lg border border-[#1D4ED8]/20">
                          {q.category}
                        </span>
                      )}
                    </div>
                  </label>
                );
              })}
              {loading && <div className="px-6 py-4 text-sm text-muted-foreground font-mono animate-pulse">Loading…</div>}
              {!loading && questions.length === 0 && <div className="px-6 py-4 text-sm text-muted-foreground">No approved questions in this round.</div>}
            </div>
            <div className="px-6 py-4 border-t border-border flex items-center justify-between gap-3 flex-shrink-0">
              <div>
                {!loading && questions.length < total && (
                  <button onClick={() => loadQuestions(round, offset)}
                    className="font-mono text-xs px-3 py-1.5 rounded-xl border border-border text-muted-foreground hover:border-foreground hover:text-foreground transition-colors">
                    Load 10 more ({total - questions.length} remaining)
                  </button>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button onClick={onClose} className="font-mono text-xs text-muted-foreground hover:text-foreground transition-colors">Cancel</button>
                <button onClick={doImport} disabled={selected.size === 0}
                  className={`px-4 py-2 text-sm font-bold border transition-all ${selected.size > 0 ? "bg-[#7C3AED] text-white border-[#7C3AED] hover:bg-[#6D28D9]" : "bg-secondary text-muted-foreground border-border cursor-not-allowed opacity-50"}`}>
                  Import {selected.size > 0 ? `${selected.size} question${selected.size !== 1 ? "s" : ""}` : "selected"} →
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function InterviewSetupTab({ role, visible }) {
  const { user } = useAuth();
  const canEdit = !user || user.role !== "company_employee" || role.company_user_id === user.user_id;
  const [questions, setQuestions] = useState([]);
  const [refDocName, setRefDocName] = useState(null);
  const [loadingSetup, setLoadingSetup] = useState(true);
  const [rdGenerating, setRdGenerating] = useState(false);
  const [rdGeneratingMore, setRdGeneratingMore] = useState(false);
  const [jdGenerating, setJdGenerating] = useState(false);
  const [jdGeneratingMore, setJdGeneratingMore] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [bankMatch, setBankMatch] = useState(null);
  const [showBankModal, setShowBankModal] = useState(false);
  const [newlyAddedId, setNewlyAddedId] = useState(null);
  const [inviteExpiresDays, setInviteExpiresDays] = useState(null);
  const [retakesPerQuestion, setRetakesPerQuestion] = useState(1);
  const [wizardStep, setWizardStep] = useState(1);

  // Consent config state
  const [consentConfig, setConsentConfig] = useState({ consent_enabled: false, require_work_authorization: true, require_notice_period: false, require_earliest_start: false });
  const [savedConsentConfig, setSavedConsentConfig] = useState(null);
  const [savingConsent, setSavingConsent] = useState(false);
  const [templateCustomized, setTemplateCustomized] = useState(false);
  const [consentTemplateBody, setConsentTemplateBody] = useState("");
  const [consentPrivacyEmail, setConsentPrivacyEmail] = useState("");
  const [consentCompanyName, setConsentCompanyName] = useState("");
  const [showConsentPreview, setShowConsentPreview] = useState(false);

  const docFileRef = useRef();
  const silentSaveRef = useRef(null);

  // Auto-save questions+settings when tab is switched away
  const prevVisibleRef = useRef(visible);
  useEffect(() => {
    if (prevVisibleRef.current && !visible) {
      silentSaveRef.current?.();
    }
    prevVisibleRef.current = visible;
  }, [visible]);

  useEffect(() => {
    (async () => {
      try {
        const [setupRes, matchRes, consentRes] = await Promise.all([
          api.get(`/company/roles/${role.company_role_id}/interview-setup`),
          api.get(`/company/roles/${role.company_role_id}/interview-setup/bank-match`).catch(() => ({ data: { matched: false } })),
          api.get(`/company/roles/${role.company_role_id}/consent-config`).catch(() => ({ data: { consent_enabled: false, require_work_authorization: true, require_notice_period: false, require_earliest_start: false } })),
        ]);
        setQuestions(setupRes.data.questions || []);
        setRefDocName(setupRes.data.reference_doc_name || null);
        setInviteExpiresDays(setupRes.data.invite_expires_days ?? null);
        setRetakesPerQuestion(setupRes.data.retakes_per_question ?? 1);
        if (matchRes.data.matched) setBankMatch(matchRes.data);
        const { template_customized, template_body, privacy_email, company_name, ...consentData } = consentRes.data;
        setConsentConfig(consentData);
        setSavedConsentConfig(consentData);
        setTemplateCustomized(!!template_customized);
        setConsentTemplateBody(template_body || "");
        setConsentPrivacyEmail(privacy_email || "");
        setConsentCompanyName(company_name || "");
      } finally { setLoadingSetup(false); }
    })();
  }, [role.company_role_id]);

  const saveConsentConfig = async () => {
    setSavingConsent(true);
    try {
      const { data } = await api.put(`/company/roles/${role.company_role_id}/consent-config`, consentConfig);
      setSavedConsentConfig({ ...consentConfig });
      toast.success(data.consent_enabled ? "Consent form enabled for this role" : "Consent form disabled");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save consent settings");
    } finally { setSavingConsent(false); }
  };

  const consentDirty = savedConsentConfig && JSON.stringify(consentConfig) !== JSON.stringify(savedConsentConfig);

  const makeId = () => `q_${Math.random().toString(36).slice(2, 10)}`;

  const importBankQuestions = (bankQs) => {
    const existingTexts = new Set(questions.map((q) => q.text.toLowerCase()));
    const newQs = bankQs
      .filter((bq) => !existingTexts.has(bq.text.toLowerCase()))
      .map((bq) => ({
        id: makeId(),
        text: bq.text,
        key_points: bq.key_points || [],
        sample_answer: bq.sample_answer || "",
        source: "admin_bank",
        bank: "jd",
        order: 0,
        selected: false,
      }));
    if (!newQs.length) { toast.info("All selected questions are already in the list"); return; }
    setQuestions((prev) => [...newQs, ...prev].map((q, i) => ({ ...q, order: i })));
    toast.success(`${newQs.length} question${newQs.length !== 1 ? "s" : ""} imported — review and select to include`);
  };

  const generateQuestions = async (bank, isMore = false) => {
    const bankQs = questions.filter((q) => (q.bank || "jd") === bank);
    const offset = isMore ? bankQs.filter((q) => q.source === "ai_generated").length : 0;
    if (bank === "ref_doc") { isMore ? setRdGeneratingMore(true) : setRdGenerating(true); }
    else { isMore ? setJdGeneratingMore(true) : setJdGenerating(true); }
    try {
      const { data } = await api.post(
        `/company/roles/${role.company_role_id}/interview-setup/generate-questions?offset=${offset}&bank=${bank}`
      );
      const newQs = data.questions || [];
      if (!newQs.length) { toast.info("No new questions generated"); return; }
      const existingTexts = new Set(questions.map((q) => q.text.toLowerCase()));
      const deduped = newQs.filter((q) => !existingTexts.has(q.text.toLowerCase()));
      const merged = [...questions, ...deduped].map((q, i) => ({ ...q, order: i }));
      setQuestions(merged);
      silentSave(merged);
      toast.success(`${newQs.length} question${newQs.length !== 1 ? "s" : ""} ${isMore ? "appended" : "generated"}`);
      // Thin-JD notice: the model generated fewer, grounded questions on purpose.
      if (data.jd_quality_notice) {
        toast.info(`Fewer questions generated — ${data.jd_quality_notice} Add more detail to the job description for a fuller set.`, { duration: 8000 });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Question generation failed — try again");
    } finally {
      if (bank === "ref_doc") { isMore ? setRdGeneratingMore(false) : setRdGenerating(false); }
      else { isMore ? setJdGeneratingMore(false) : setJdGenerating(false); }
    }
  };

  const addManual = (bank) => {
    const newQ = { id: makeId(), text: "", answer: "", source: "manual", bank, order: 0, selected: true };
    setNewlyAddedId(newQ.id);
    setQuestions((prev) => [newQ, ...prev]);
  };

  const onChange = (id, patch) => setQuestions((prev) => prev.map((q) => q.id === id ? { ...q, ...patch } : q));
  const onDelete = (id) => setDeleteConfirm(id);
  const confirmDelete = () => { setQuestions((prev) => prev.filter((q) => q.id !== deleteConfirm)); setDeleteConfirm(null); };

  const moveUp = (index) => {
    if (index === 0) return;
    setQuestions((prev) => {
      const arr = [...prev];
      [arr[index - 1], arr[index]] = [arr[index], arr[index - 1]];
      return arr.map((q, i) => ({ ...q, order: i }));
    });
  };
  const moveDown = (index) => {
    setQuestions((prev) => {
      if (index === prev.length - 1) return prev;
      const arr = [...prev];
      [arr[index], arr[index + 1]] = [arr[index + 1], arr[index]];
      return arr.map((q, i) => ({ ...q, order: i }));
    });
  };

  // key_points edits are handled directly inside QuestionCard via onChange

  const saveSetup = async () => {
    const valid = questions.filter((q) => q.text.trim());
    if (!valid.length) { toast.error("Add at least one question before saving"); return; }
    setSaving(true);
    try {
      const { data } = await api.put(`/company/roles/${role.company_role_id}/interview-setup`, { questions: valid, invite_expires_days: inviteExpiresDays, retakes_per_question: retakesPerQuestion });
      toast.success(`Interview setup saved — ${data.question_count} question${data.question_count !== 1 ? "s" : ""} selected`);
    } catch { toast.error("Save failed — try again"); }
    finally { setSaving(false); }
  };

  const silentSave = async (qs) => {
    const valid = qs.filter((q) => q.text.trim());
    if (!valid.length) return;
    try { await api.put(`/company/roles/${role.company_role_id}/interview-setup`, { questions: valid, invite_expires_days: inviteExpiresDays, retakes_per_question: retakesPerQuestion }); }
    catch { /* silent — user can still save manually */ }
  };

  // Keep silentSaveRef pointing to latest closure so the auto-save effect stays fresh
  silentSaveRef.current = () => silentSave(questions);

  const handleNextToSettings = async () => {
    setSaving(true);
    try { await silentSave(questions); } finally { setSaving(false); }
    setWizardStep(2);
  };

  const handleNextToConsent = async () => {
    setSaving(true);
    try { await silentSave(questions); } finally { setSaving(false); }
    setWizardStep(3);
  };

  const uploadDoc = async (file) => {
    if (!file) return;
    setUploadingDoc(true);
    try {
      const fd = new FormData();
      fd.append("doc_file", file);
      const { data } = await api.post(
        `/company/roles/${role.company_role_id}/interview-setup/reference-doc`, fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setRefDocName(data.doc_name);
      toast.success(`Reference document uploaded (${(data.char_count / 1000).toFixed(1)}k chars)`);
    } catch { toast.error("Upload failed"); }
    finally { setUploadingDoc(false); }
  };

  const removeDoc = async () => {
    try {
      await api.delete(`/company/roles/${role.company_role_id}/interview-setup/reference-doc`);
      setRefDocName(null);
      toast.success("Reference document removed");
    } catch { toast.error("Could not remove document"); }
  };

  const rdQuestions = questions.filter((q) => (q.bank || "jd") === "ref_doc");
  const jdQuestions = questions.filter((q) => (q.bank || "jd") === "jd");
  const rdSelected = rdQuestions.filter((q) => q.selected && q.text.trim()).length;
  const jdSelected = jdQuestions.filter((q) => q.selected && q.text.trim()).length;
  const selectedCount = rdSelected + jdSelected;

  if (loadingSetup) return <div className="p-12 font-mono text-sm text-muted-foreground text-center">Loading setup…</div>;

  const STEPS = [
    { step: 1, label: "QUESTIONS" },
    { step: 2, label: "SETTINGS" },
    { step: 3, label: "CONSENT" },
  ];

  return (
    <div className="p-6 space-y-6">
      {/* ── Wizard progress indicator ── */}
      <div className="flex items-center gap-0 flex-wrap">
        {STEPS.map((s, i) => (
          <React.Fragment key={s.step}>
            <button
              type="button"
              onClick={() => wizardStep > s.step && setWizardStep(s.step)}
              className={`flex items-center gap-2 px-4 py-2.5 font-mono text-[10px] tracking-[0.12em] border transition-all ${
                wizardStep === s.step
                  ? "bg-[#0025D8] text-white border-[#0025D8]"
                  : wizardStep > s.step
                  ? "text-[#10B981] border-[#10B981]/30 bg-[#10B981]/5 hover:bg-[#10B981]/10 cursor-pointer"
                  : "text-muted-foreground border-border bg-secondary/40 cursor-default"
              }`}
            >
              <span className="w-4 h-4 flex items-center justify-center font-bold">
                {wizardStep > s.step ? <Check className="w-3 h-3" /> : s.step}
              </span>
              {s.label}
            </button>
            {i < STEPS.length - 1 && (
              <div className={`h-px w-5 flex-shrink-0 ${wizardStep > i + 1 ? "bg-[#10B981]/40" : "bg-border"}`} />
            )}
          </React.Fragment>
        ))}
        <div className="flex-1" />
        {selectedCount > 0 && (
          <span className="font-mono text-[10px] text-muted-foreground">
            {selectedCount} question{selectedCount !== 1 ? "s" : ""} selected
          </span>
        )}
      </div>

      {/* ── Step 1: Questions ── */}
      {wizardStep === 1 && (
        <div className="space-y-5">
          {/* Source toolbars */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* JD bank */}
            <div className="rounded-lg border border-[#0025D8]/20 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <span className="font-mono text-[9px] px-1.5 py-0.5 bg-[#0025D8] text-white tracking-[0.1em] font-bold">JD</span>
                <span className="font-mono text-[10px] text-muted-foreground tracking-[0.15em]">FROM JOB DESCRIPTION</span>
                <span className="ml-auto font-mono text-[10px] text-[#0025D8] font-semibold">{jdSelected} selected</span>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {jdQuestions.length === 0 ? (
                  <button onClick={() => generateQuestions("jd")} disabled={jdGenerating}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg border bg-[#0025D8] text-white border-[#0025D8] hover:bg-[#001CB3] transition-all disabled:opacity-50">
                    <Sparkles className={`w-3.5 h-3.5 ${jdGenerating ? "animate-pulse" : ""}`} />
                    {jdGenerating ? "Generating…" : "Generate 10 Questions"}
                  </button>
                ) : (
                  <>
                    <button onClick={() => generateQuestions("jd")} disabled={jdGenerating || jdGeneratingMore}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-bold rounded-lg border bg-[#0025D8]/8 text-[#0025D8] border-[#0025D8]/40 hover:bg-[#0025D8] hover:text-white transition-all disabled:opacity-50">
                      <RotateCcw className={`w-3 h-3 ${jdGenerating ? "animate-spin" : ""}`} />
                      {jdGenerating ? "Regenerating…" : "Regenerate"}
                    </button>
                    <button onClick={() => generateQuestions("jd", true)} disabled={jdGenerating || jdGeneratingMore}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-bold rounded-lg border bg-[#0025D8]/8 text-[#0025D8] border-[#0025D8]/40 hover:bg-[#0025D8] hover:text-white transition-all disabled:opacity-50">
                      <Sparkles className={`w-3 h-3 ${jdGeneratingMore ? "animate-pulse" : ""}`} />
                      {jdGeneratingMore ? "Generating…" : "+5 More"}
                    </button>
                  </>
                )}
                <button onClick={() => addManual("jd")}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-bold rounded-lg border border-[#0025D8]/30 text-[#0025D8] hover:bg-[#0025D8]/8 transition-all">
                  <Plus className="w-3 h-3" /> Manual
                </button>
                {bankMatch && (
                  <button onClick={() => setShowBankModal(true)}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-bold border bg-[#7C3AED]/8 text-[#7C3AED] border-[#7C3AED]/30 hover:bg-[#7C3AED] hover:text-white transition-all">
                    <Star className="w-3 h-3" /> Bank
                  </button>
                )}
              </div>
            </div>

            {/* Ref Doc bank */}
            <div className="rounded-lg border border-[#7C3AED]/20 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <span className="font-mono text-[9px] px-1.5 py-0.5 bg-[#7C3AED] text-white tracking-[0.1em] font-bold">DOC</span>
                <span className="font-mono text-[10px] text-muted-foreground tracking-[0.15em]">FROM REFERENCE DOC</span>
                <span className="ml-auto font-mono text-[10px] text-[#7C3AED] font-semibold">{rdSelected} selected</span>
              </div>
              <input ref={docFileRef} type="file" accept=".pdf,.docx,.doc,.txt" className="hidden"
                onChange={(e) => uploadDoc(e.target.files[0])} />
              {refDocName ? (
                <div className="flex items-center gap-2 p-2 rounded-lg border border-[#10B981]/30 bg-[#10B981]/5">
                  <FileText className="w-3.5 h-3.5 text-[#10B981] flex-shrink-0" />
                  <span className="flex-1 truncate text-xs font-medium">{refDocName}</span>
                  <button onClick={() => docFileRef.current?.click()} disabled={uploadingDoc}
                    className="font-mono text-[9px] px-1.5 py-0.5 rounded-xl border border-border text-muted-foreground hover:text-foreground transition-colors">Replace</button>
                  <button onClick={removeDoc} className="text-muted-foreground hover:text-[#E11D48] transition-colors"><X className="w-3.5 h-3.5" /></button>
                </div>
              ) : (
                <div
                  onClick={() => !uploadingDoc && docFileRef.current?.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { e.preventDefault(); uploadDoc(e.dataTransfer.files[0]); }}
                  className="border border-dashed border-[#7C3AED]/30 p-3 text-center cursor-pointer hover:border-[#7C3AED]/60 hover:bg-[#7C3AED]/5 transition-all">
                  {uploadingDoc
                    ? <><RotateCcw className="w-4 h-4 text-[#7C3AED] animate-spin mx-auto mb-1" /><p className="font-mono text-[10px] text-muted-foreground">Uploading…</p></>
                    : <><Upload className="w-4 h-4 text-[#7C3AED] mx-auto mb-1" /><p className="text-xs text-muted-foreground">Upload PDF, DOCX, TXT</p></>
                  }
                </div>
              )}
              <div className="flex items-center gap-2 flex-wrap">
                {rdQuestions.length === 0 ? (
                  <button onClick={() => generateQuestions("ref_doc")} disabled={rdGenerating || !refDocName}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold border bg-[#7C3AED] text-white border-[#7C3AED] hover:bg-[#6D28D9] transition-all disabled:opacity-40 disabled:cursor-not-allowed">
                    <Sparkles className={`w-3.5 h-3.5 ${rdGenerating ? "animate-pulse" : ""}`} />
                    {rdGenerating ? "Generating…" : "Generate 10 Questions"}
                  </button>
                ) : (
                  <>
                    <button onClick={() => generateQuestions("ref_doc")} disabled={rdGenerating || rdGeneratingMore || !refDocName}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-bold border bg-[#7C3AED]/8 text-[#7C3AED] border-[#7C3AED]/40 hover:bg-[#7C3AED] hover:text-white transition-all disabled:opacity-40">
                      <RotateCcw className={`w-3 h-3 ${rdGenerating ? "animate-spin" : ""}`} />
                      {rdGenerating ? "Regenerating…" : "Regenerate"}
                    </button>
                    <button onClick={() => generateQuestions("ref_doc", true)} disabled={rdGenerating || rdGeneratingMore || !refDocName}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-bold border bg-[#7C3AED]/8 text-[#7C3AED] border-[#7C3AED]/40 hover:bg-[#7C3AED] hover:text-white transition-all disabled:opacity-40">
                      <Sparkles className={`w-3 h-3 ${rdGeneratingMore ? "animate-pulse" : ""}`} />
                      {rdGeneratingMore ? "Generating…" : "+5 More"}
                    </button>
                  </>
                )}
                <button onClick={() => addManual("ref_doc")}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-bold rounded-lg border border-[#7C3AED]/30 text-[#7C3AED] hover:bg-[#7C3AED]/8 transition-all">
                  <Plus className="w-3 h-3" /> Manual
                </button>
                {!refDocName && rdQuestions.length === 0 && (
                  <span className="font-mono text-[9px] text-amber-500">↑ upload doc to enable</span>
                )}
              </div>
            </div>
          </div>

          {/* Unified questions list */}
          {questions.length > 0 && (
            <div className="space-y-2">
              <div className="font-mono text-[10px] text-muted-foreground tracking-[0.15em]">ALL QUESTIONS · {questions.length} total</div>
              {questions.map((q, idx) => (
                <QuestionCard key={q.id} q={q} index={idx} total={questions.length} role={role}
                  onChange={onChange} onDelete={onDelete}
                  onMoveUp={() => moveUp(idx)} onMoveDown={() => moveDown(idx)}
                  autoFocus={q.id === newlyAddedId} onFocused={() => setNewlyAddedId(null)}
                  showBank />
              ))}
            </div>
          )}

          {/* Step 1 navigation */}
          <div className="flex items-center justify-between pt-4 border-t border-border">
            <span className="font-mono text-[10px] text-muted-foreground">
              {selectedCount > 0
                ? `${selectedCount} question${selectedCount !== 1 ? "s" : ""} selected — ${rdSelected} DOC · ${jdSelected} JD`
                : "Select questions to include in the interview"}
            </span>
            <button onClick={handleNextToSettings} disabled={saving}
              className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold bg-[#0025D8] text-white rounded-lg border border-[#0025D8] hover:bg-[#001CB3] transition-all disabled:opacity-50">
              {saving ? "Saving…" : "Next: Settings"} <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* ── Step 2: Settings ── */}
      {wizardStep === 2 && (
        <div className="space-y-6">
          <div>
            <h3 className="font-display font-bold text-base">Interview Settings</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Configure invite link behaviour for this role.</p>
          </div>

          <div className="space-y-2">
            <div className="font-mono text-[10px] text-muted-foreground tracking-[0.15em]">INVITE LINK EXPIRES</div>
            <div className="flex items-center gap-2 flex-wrap">
              {[{ label: "1 Day", value: 1 }, { label: "3 Days", value: 3 }, { label: "7 Days", value: 7 }, { label: "No Expiry", value: null }].map(({ label, value }) => (
                <button key={label} type="button" onClick={() => setInviteExpiresDays(value)}
                  className={`font-mono text-[10px] px-4 py-2 border transition-all ${
                    inviteExpiresDays === value
                      ? "bg-[#0025D8] text-white border-[#0025D8]"
                      : "text-muted-foreground border-border hover:border-[#0025D8] hover:text-[#0025D8]"
                  }`}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <div className="font-mono text-[10px] text-muted-foreground tracking-[0.15em]">RETAKES PER QUESTION</div>
            <div className="flex items-center gap-2 flex-wrap">
              {[{ label: "0 — Strict", value: 0 }, { label: "1 Retake", value: 1 }, { label: "2 Retakes", value: 2 }].map(({ label, value }) => (
                <button key={value} type="button" onClick={() => setRetakesPerQuestion(value)}
                  className={`font-mono text-[10px] px-4 py-2 border transition-all ${
                    retakesPerQuestion === value
                      ? "bg-[#0025D8] text-white border-[#0025D8]"
                      : "text-muted-foreground border-border hover:border-[#0025D8] hover:text-[#0025D8]"
                  }`}>
                  {label}
                </button>
              ))}
            </div>
            <p className="font-mono text-[10px] text-[#F59E0B]">
              {retakesPerQuestion === 0
                ? "Answers locked after first recording — strictest integrity."
                : retakesPerQuestion === 1
                ? "One do-over per question — recommended for most roles."
                : "Two retakes — suitable for junior or graduate hiring."}
            </p>
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-border">
            <button onClick={() => setWizardStep(1)}
              className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold rounded-xl border border-border text-muted-foreground hover:text-foreground hover:border-foreground transition-all">
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
            <button onClick={handleNextToConsent} disabled={saving}
              className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold bg-[#0025D8] text-white rounded-lg border border-[#0025D8] hover:bg-[#001CB3] transition-all disabled:opacity-50">
              {saving ? "Saving…" : "Next: Consent"} <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* ── Step 3: Consent ── */}
      {wizardStep === 3 && (
        <div className="space-y-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ShieldCheck className="w-4 h-4 text-[#0025D8]" />
              <div>
                <h3 className="font-display font-bold text-base">Consent Form</h3>
                <p className="text-xs text-muted-foreground mt-0.5">Collect legal consent and candidate screening questions on the apply link.</p>
              </div>
            </div>
            {canEdit && (
              <Link to="/company/consent" className="flex items-center gap-1 font-mono text-[10px] text-muted-foreground hover:text-[#0025D8] transition-colors rounded-xl border border-border px-2.5 py-1.5">
                <ExternalLink className="w-3 h-3" />
                MANAGE TEMPLATE
              </Link>
            )}
          </div>

          <label className={`flex items-center gap-3 select-none ${canEdit ? "cursor-pointer" : "cursor-not-allowed opacity-60"}`}>
            <div
              onClick={() => canEdit && setConsentConfig((c) => ({ ...c, consent_enabled: !c.consent_enabled }))}
              className={`relative w-10 h-5 rounded-full transition-colors flex-shrink-0 ${canEdit ? "cursor-pointer" : "cursor-not-allowed"} ${consentConfig.consent_enabled ? "bg-[#0025D8]" : "bg-border"}`}
            >
              <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${consentConfig.consent_enabled ? "translate-x-5" : "translate-x-0.5"}`} />
            </div>
            <div>
              <div className="text-sm font-semibold">{consentConfig.consent_enabled ? "Consent form enabled" : "Consent form disabled"}</div>
              <div className="font-mono text-[10px] text-muted-foreground mt-0.5">
                {consentConfig.consent_enabled ? "Candidates must agree before submitting" : "No consent step on the apply link"}
              </div>
            </div>
          </label>

          {consentConfig.consent_enabled && !templateCustomized && (
            <div className="flex items-start gap-3 p-3.5 bg-[#F59E0B]/8 rounded-lg border border-[#F59E0B]/40">
              <AlertTriangle className="w-4 h-4 text-[#F59E0B] flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="font-mono text-[10px] font-bold tracking-[0.15em] text-[#B45309] mb-0.5">DEFAULT TEMPLATE IN USE</div>
                <p className="text-xs text-[#92400E] leading-snug">
                  Candidates will see placeholder text like <span className="font-mono font-bold">[Company Name]</span> and <span className="font-mono font-bold">[privacy@companyemail.com]</span>.{" "}
                  <Link to="/company/consent" className="underline font-semibold hover:text-[#78350F] transition-colors">Customise your template →</Link>
                </p>
              </div>
            </div>
          )}

          {consentConfig.consent_enabled && (
            <div className="space-y-3 border-t border-border pt-4">
              <div className="font-mono text-[9px] tracking-[0.2em] text-muted-foreground">OPTIONAL QUESTIONS</div>
              {[
                { key: "require_work_authorization", label: "Work Authorization", desc: "Ask candidate to select their work authorization status" },
                { key: "require_notice_period", label: "Notice Period", desc: "How many days notice the candidate must give their current employer" },
                { key: "require_earliest_start", label: "Earliest Start Date", desc: "When the candidate can start if hired" },
              ].map(({ key, label, desc }) => (
                <label key={key} className={`flex items-center gap-3 ${canEdit ? "cursor-pointer" : "cursor-not-allowed opacity-60"}`}>
                  <input
                    type="checkbox"
                    checked={consentConfig[key]}
                    onChange={(e) => canEdit && setConsentConfig((c) => ({ ...c, [key]: e.target.checked }))}
                    disabled={!canEdit}
                    className="w-3.5 h-3.5 accent-[#0025D8]"
                  />
                  <div>
                    <div className="text-sm font-medium">{label}</div>
                    <div className="font-mono text-[10px] text-muted-foreground">{desc}</div>
                  </div>
                </label>
              ))}
            </div>
          )}

          {consentTemplateBody && (
            <div className="rounded-xl border border-border">
              <button type="button" onClick={() => setShowConsentPreview((p) => !p)}
                className="w-full flex items-center justify-between px-4 py-2.5 bg-secondary hover:bg-secondary/80 transition-colors">
                <span className="flex items-center gap-2 font-mono text-[10px] tracking-[0.15em] text-muted-foreground">
                  {showConsentPreview ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  {showConsentPreview ? "HIDE PREVIEW" : "PREVIEW CANDIDATE VIEW"}
                </span>
              </button>
              {showConsentPreview && (
                <div className="p-4 bg-[#FAFAFA] border-t border-border">
                  <div className="font-mono text-[9px] tracking-[0.2em] text-muted-foreground mb-3">WHAT CANDIDATES WILL SEE</div>
                  <pre className="text-xs text-foreground whitespace-pre-wrap leading-relaxed font-mono">
                    {consentTemplateBody
                      .replace(/\[Full Name\]/g, "Jane Smith")
                      .replace(/\[Company Name\]/g, consentCompanyName || "[Company Name]")
                      .replace(/\[privacy@companyemail\.com\]/g, consentPrivacyEmail || "[privacy@companyemail.com]")}
                  </pre>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center justify-between pt-4 border-t border-border">
            <button onClick={() => setWizardStep(2)}
              className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold rounded-xl border border-border text-muted-foreground hover:text-foreground hover:border-foreground transition-all">
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
            {canEdit ? (
              <button onClick={saveConsentConfig} disabled={savingConsent || !consentDirty}
                className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold bg-[#0025D8] text-white rounded-lg border border-[#0025D8] hover:bg-[#001CB3] transition-all disabled:opacity-40 disabled:cursor-not-allowed">
                <Save className="w-4 h-4" />
                {savingConsent ? "Saving…" : consentDirty ? "Save Consent" : "Saved"}
              </button>
            ) : (
              <span className="font-mono text-[10px] text-muted-foreground tracking-[0.1em]">VIEW ONLY</span>
            )}
          </div>
        </div>
      )}

      {deleteConfirm && (
        <ConfirmDialog
          danger
          message="Remove this question from the interview setup?"
          confirmLabel="Delete"
          onConfirm={confirmDelete}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}

      {showBankModal && bankMatch && (
        <BankImportModal
          role={role}
          bankMatch={bankMatch}
          onImport={importBankQuestions}
          onClose={() => setShowBankModal(false)}
        />
      )}
    </div>
  );
}

// ── Main Dashboard ────────────────────────────────────────────────────────────

export default function CompanyRoleDashboard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const tab = searchParams.get("tab") || "candidates";

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/company/roles/${id}`); setRole(data); }
      finally { setLoading(false); }
    })();
  }, [id]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/company/roles/${id}`);
      toast.success(`"${role.title}" deleted`);
      navigate("/company/roles");
    } catch { toast.error("Could not delete role — try again"); setDeleting(false); }
  };

  if (loading) return <div className="p-12 font-mono text-sm text-muted-foreground">Loading role…</div>;
  if (!role) return (
    <div className="p-12 text-center">
      <p className="font-semibold mb-2">Role not found.</p>
      <Link to="/company/roles" className="text-[#0025D8] hover:underline text-sm font-mono">← Back to roles</Link>
    </div>
  );

  return (
    <main className="max-w-7xl mx-auto px-6 md:px-12 py-10">
      <div className="flex items-center gap-1.5 font-mono text-[10px] text-muted-foreground mb-6">
        <Link to="/company/roles" className="hover:text-foreground">ROLES</Link>
        <ChevronRight className="w-3 h-3" />
        <span className="text-foreground truncate max-w-[200px]">{role.title.toUpperCase()}</span>
      </div>
      <div className="rounded-xl border border-border bg-white">
        <RoleHeader
          role={{ ...role, onDelete: () => setDeleteConfirm(true) }}
          onStatusChange={(status) => setRole((r) => ({ ...r, status }))}
          onSeniorityChange={(level) => setRole((r) => ({ ...r, seniority_override: level, seniority_level: level }))}
          onRoleUpdate={(updates) => setRole((r) => ({ ...r, ...updates }))}
        />
        <TabBar active={tab} onChange={(t) => setSearchParams({ tab: t })} />
        <div className={tab !== "candidates" ? "hidden" : ""}><CandidatesTab role={role} tab={tab} /></div>
        <div className={tab === "candidates" ? "hidden" : ""}><InterviewSetupTab role={role} visible={tab !== "candidates"} /></div>
      </div>

      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl border border-border max-w-sm w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 bg-[#E11D48]/10 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-4.5 h-4.5 text-[#E11D48]" />
              </div>
              <div>
                <p className="font-semibold text-sm">Delete this role?</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  <strong>{role.title}</strong> and all its candidates, interview setup, and invites will be permanently removed.
                </p>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setDeleteConfirm(false)} disabled={deleting}
                className="px-4 py-2 text-sm rounded-xl border border-border text-muted-foreground hover:text-foreground transition-colors">
                Cancel
              </button>
              <button onClick={handleDelete} disabled={deleting}
                className="px-4 py-2 text-sm font-bold bg-[#E11D48] text-white hover:bg-[#be1238] transition-colors disabled:opacity-60">
                {deleting ? "Deleting…" : "Delete Role"}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
